import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { ComponentType, ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useNavigate, useSearchParams } from "react-router";

import { Button, Container, Icon, PortalLayout, PortalPanelBody, SampleDataContext, Typography } from "./design-system";
import type { StepperItemData } from "./design-system";
import { resolveCapability } from "./capability";
import { CapabilityProvider, useCapability } from "./capability/use-capability";
import { getCurrentRawClaim } from "./capability/session-claim-source";
import { ConfirmDetailsRoute } from "./features/confirm-details/ConfirmDetailsRoute";
import { DeclarationRoute } from "./features/declaration/DeclarationRoute";
import type { DeclarationServerFieldErrors, DeclarationSubmission } from "./features/declaration/DeclarationRoute";
import { toDeclarationServerFieldErrors } from "./features/declaration/server-field-errors";
import { ExposuresJourneyRoute } from "./features/renewal-capture/ExposuresJourneyRoute";
import { applySectionListStatus, buildSectionListRows } from "./features/renewal-capture/section-status";
import { expandSectionInstances } from "./state/section-instances";
import { ReceivedRoute } from "./features/received/ReceivedRoute";
import { RenewalPreviewRoute } from "./features/renewal-preview/RenewalPreviewRoute";
import { RenewalLanding } from "./features/renewal-landing/RenewalLanding";
import type { LandingRetryResult } from "./features/renewal-landing/RenewalLanding";
import { CheckEmailPage } from "./routes/check-email/CheckEmailPage";
import { LinkUnavailablePage } from "./routes/link-unavailable/LinkUnavailablePage";
import { NotPermittedPage } from "./routes/not-permitted/NotPermittedPage";
import { RedeemPage } from "./routes/redeem/RedeemPage";
import { RenewalStatusPage } from "./routes/renewal-status/RenewalStatusPage";
import { SignInPage } from "./routes/sign-in/SignInPage";
import {
  bootstrapSessionFromRefreshCookie,
  getRefreshCompletionCount,
  getSessionToken,
  LINK_UNAVAILABLE_ORIGIN,
  listAssignments,
  recordSectionConfirmation,
  registerSessionEndedHandler,
  SIGN_IN_ORIGIN_PARAM,
  withSignInRef,
} from "./portal-auth";
import { subscribeTokenChange } from "./portal-auth/token";
import {
  BUSINESS_DETAILS_SECTION_ID,
  decodeSectionInstanceId,
  isSectionConfirmed,
  SECTION_TABLE,
  STEP_TABLE,
  STEPPER_STAGE_TABLE,
  toConfirmedAnswers,
  toRenewalData,
} from "./routing/route-table.ts";
import type { ConfirmedAnswers, RenewalData, SectionId, SectionInstanceId, StepId } from "./routing/route-table.ts";
import { buildStepRoutes, PageErrorShell } from "./routing/router.tsx";
import type { RouterDependencies, StepGuardContext, StepRouteComponents } from "./routing/router.tsx";
import { resolveBackNavigationTarget, resolveResumeTarget } from "./routing/resume.ts";
import { computeSectionStates, computeStepperStages } from "./routing/section-state.ts";
import type { SectionStateEntry } from "./routing/types.ts";
import { getStageLabel, getStepperStatusText } from "./features/renewal-landing/content.ts";
import { pendingShellCopy, reloadFailureCopy } from "./routing/reload-failure-copy.ts";
import type { RenewalSummaryPort, RenewalSummaryReadResult } from "./state/renewal-summary-port";
import type { OnFilePort } from "./state/on-file-port";
import type { AccountReadResult, RenewalDataPort } from "./state/renewal-data-port";
import type { WorkingCopyPort } from "./state/working-copy-port";
import type {
  DeclarationSubmissionFailureDetail,
  DeclarationSubmissionPort,
  SectionCompletionState,
  SectionCompletionStatus,
} from "./state/declaration-submission-port";
import { DECLARATION_SUBMISSION_FAILURE_CODES } from "./state/declaration-submission-port";
import { createHttpRenewalSummaryPort } from "./state/renewal-summary-client";
import { createHttpOnFilePort } from "./state/http/on-file.http";
import { createHttpRenewalDataPort } from "./state/http/renewal-data.http";
import { createHttpWorkingCopyPort } from "./state/http/working-copy.http";
import { createHttpDeclarationSubmissionPort } from "./state/http/declaration-submission-client";
import { readSubmissionStatus } from "./state/submission-status-client";

/**
 * The application shell's composition root (TG-12). Wires the already-
 * built, already-tested outputs of TG-1 (route-table.ts, resume.ts,
 * section-state.ts), TG-2 (WorkingCopyPort/RenewalSummaryPort), TG-4
 * (router.tsx's `buildStepRoutes`) and the real feature route components
 * (TG-5/TG-6/TG-7) into one served router tree — the composition gap
 * Functional Evaluation Layer 2 found (ADR-20260930-zzw2): no task group
 * in the original 11-group plan required this step, so `App.tsx` rendered
 * only the milestone-scaffold placeholder while every route beneath it
 * sat correctly implemented and tested, but unreachable by a real
 * customer.
 *
 * Human direction (2026-09-30, Harry Henry): the landing route is always
 * the customer's first page for this feature — an overview with exactly
 * one primary action (AC-1/RenewalLanding's own contract), never an
 * automatic redirect past it. `"/"` therefore mounts `RenewalLanding`
 * directly, composed with the four step routes `buildStepRoutes` derives
 * from `STEP_TABLE` (ADR-20260826-srf0: `createBrowserRouter`, history-API
 * paths — no hash routing).
 *
 * This file invents no new resume or guard logic: `loadGuardContext`
 * below only calls TG-1's own `computeSectionStates`/`resolveResumeTarget`
 * against a fresh read of the same two ports every route already
 * consumes — the identical shape `test/support/renewal-capture-harness.tsx`
 * established for exactly this composition, before any task group in this
 * feature's plan owned wiring it into the real `App.tsx`.
 *
 * ADR-20260826-vbpt (carried forward): no section id or step id appears as
 * a literal anywhere in this file except `StepRouteComponents`'
 * per-step mapping below, the one place a closed `StepId` union is
 * sanctioned as a `Record` key. Every other step id this file needs (the
 * landing route's resolved navigation target, the declaration route's
 * "one step beyond wherever resume now resolves") is read off a `StepId`
 * value TG-1's own exports already returned, or derived from
 * `StepDescriptor.order`, never typed here as a fresh string literal.
 *
 * ADR-20260824-gps6 / the adapter rule: this file imports design-system
 * components only (`Container`, `Typography`, for the loading shell) —
 * never `@mui/*` directly.
 *
 * No session/authentication boundary exists yet (D-22, no owning story) —
 * `RENEWAL_ID` and its fixture, and the "every visitor is the renewal's
 * owner" capability below, are this Milestone's stand-in, exactly the same
 * "the stub is the runtime implementation until the real one lands" shape
 * ADR-20260826-ggnj already documents for the ports themselves.
 *
 * `m1-magic-link-authentication-session` (TG-35, ADR-20261008-q2rm)
 * registers /sign-in, /sign-in/check-email and /redeem as siblings of "/"
 * in the same router. TG-35 originally served them by plain pathname
 * matching ahead of this router landing; merging develop replaced that
 * stopgap with these routes. They must be reachable before a session
 * exists, so they never sit behind `buildStepRoutes`' guard loader.
 *
 * TG-44 (the 2026-10-08 H2 amendment) adds three more anonymous siblings
 * the same way: /sign-in/link-unavailable (AC-24, ADR-20261008-evet),
 * /renewal-status (AC-28, ADR-20261008-25dm) and /renewal/not-permitted
 * (AC-27, ADR-20261008-jg8z). It also registers the session-ended
 * notification (AC-9) with `router.navigate` once the router exists,
 * below -- `router.navigate(to)` with no second argument, never a
 * `navigate()`/`<Link>` `state` option (ADR-20261006-cp4y / OBJ-92).
 */
const RENEWAL_ID = "renewal-current";

/**
 * TG-3 (m1-magic-link-portal-refresh): the real session claim, read once
 * per MOUNT of the claim-bearing route that calls this -- replaces the
 * retired D-22 stand-in (`buildStandInOwnerClaim`, deleted with
 * `session-claim-stand-in.ts`, ADR-20261008-1enh). A snapshot at mount,
 * not a reactive subscription (`useSyncExternalStore`/
 * `subscribeToSessionClaimChange`, both still available on
 * `session-claim-source.ts` for a future caller that needs one): every
 * legitimate claim change in this app coincides with a NAVIGATION (a
 * redeem success lands on "/", a session end navigates to
 * /sign-in/link-unavailable), which always mounts a fresh instance of the
 * target route's own `RequireSession` -- so a snapshot at mount already
 * observes every real transition. A reactive subscription was tried and
 * reverted (OBJ-R-/seam regression, reproduced 2026-10-15): when a
 * session ends while ITS OWN route is still mounted, the registered
 * session-ended handler already navigates explicitly
 * (`registerSessionEndedHandler`, below) -- a second, reactive navigation
 * from this hook noticing the SAME token clear raced that explicit one
 * and sometimes won, landing on `/sign-in` (state 1.1) instead of
 * `/sign-in/link-unavailable`. Reading only at mount removes the second
 * navigator entirely; the explicit handler remains the only one. This
 * file names no role and imports no claim type, consistent with
 * `session-claim-source.ts`'s own doc comment (ADR-20260826-79rv's
 * containment rule) -- `getCurrentRawClaim`'s return type is
 * `CapabilityProvider`'s own `claim` prop type, so no cast is needed here
 * either. */
function useSessionClaim(): ReturnType<typeof getCurrentRawClaim> {
  const [claim] = useState(() => getCurrentRawClaim());
  return claim;
}

/**
 * AC-24: wraps every claim-bearing route. Without a live session (no
 * token, or a token whose claim does not decode) this renders state 1.1
 * -- the sign-in route -- with no claim-derived content at all, never
 * `AccessNotConfirmed` (capability/'s own denial state for a session that
 * exists but fails to decode, a different case entirely, ADR-20261015-
 * h6ff). With a live session it mounts `CapabilityProvider` around
 * `children`, exactly where this composition root used to mount it
 * unconditionally around the whole router. `<Navigate replace>` rather
 * than an effect-driven `navigate()` call: no extra render, and no
 * history entry is pushed for the page the customer could never
 * legitimately have reached (ADR-20261006-cp4y's own "no redundant
 * history entry" preference, applied here to a guard rather than a
 * completed action).
 */
/**
 * ADR-20261016-r4bn (TG-8): a cold load (full page reload, or a brand new
 * tab) always mounts with an EMPTY in-memory token store (token.ts's own
 * contract: nothing survives a reload but the HttpOnly refresh cookie),
 * even when that cookie is still live. Before this fix, that mounted
 * `useSessionClaim`'s snapshot as `null` and rendered the sign-in redirect
 * immediately, discarding a perfectly live session. When the mount-time
 * claim is absent, this makes exactly ONE `bootstrapSessionFromRefreshCookie`
 * call -- single-flight, sharing `authenticatedFetch`'s own refresh-and-
 * retry sequence (AC-25's retry rule applies identically here: a transient
 * 5xx/429/network failure leaves the attempt to try again, never ends the
 * session) -- and holds `PendingShell` while it is in flight. On success
 * the component re-reads `getCurrentRawClaim()` fresh (not the stale
 * mount-time snapshot) and renders normally; on a definitive "ended"
 * outcome, or exhausted retries that still leave no token, it falls
 * through to the ordinary sign-in redirect below --
 * `bootstrapSessionFromRefreshCookie` already resolves a 401 to "no
 * token", so there is no separate 401-handling path here. No timer, and
 * no retry loop of this component's own: this is a ONE-SHOT call at
 * mount, never repeated while the route stays mounted.
 */
/** TG-4 (m1-declaration-next-steps), ADR-20261022-avfs: the result of the one live submission-
 * status read `RequireSession`'s own cold-load bootstrap performs. `"not-needed"` covers both "this
 * mount was already warm (no bootstrap ran at all)" and "bootstrap resolved no claim at all" --
 * neither case has anything to check status FOR. */
type ColdLoadSubmissionCheck = "not-needed" | "loading" | "submitted" | "not-submitted" | "failed";

/** OBJ-C-8/OBJ-R-7 (m1-declaration-next-steps, debate turn 2): a genuine cold load cannot be
 * detected by "no in-memory token at mount" alone -- every `/renewal/*` step route carries
 * `createStepGuardLoader` (router.tsx), which react-router runs BEFORE this component mounts, and
 * that loader's own `readSummary`/`readConfirmedSections` calls go through `authenticatedFetch`
 * ungated. On a true cold load those calls 401 and `authenticatedFetch`'s own `refreshOnce()`
 * silently mints a token from the refresh cookie before this component ever renders, so by mount
 * time `useSessionClaim()` already returns a claim and the token-presence check alone previously
 * read that as "already warm" -- skipping the cold-load submission-status read on every step route
 * (reproduced at `memory/sessions/m1-declaration-next-steps/rst-obj-r7-repro-cold-load-step-route-
 * bypass.test.tsx.txt`). This component therefore also consults
 * `getRefreshCompletionCount()` (portal-auth/authenticated-fetch.ts): a module-level counter
 * incremented every time that shared refresh sequence actually mints a token, whichever caller
 * triggered it. `lastAccountedRefreshCompletionCount` records the count this guard has already
 * reacted to; a mount that finds the live count has moved past it means some refresh happened
 * between the previous guarded mount (or page load, for the very first one) and this one, that
 * this guard has not yet checked submission status for -- exactly the loader-race case. A token
 * set by any OTHER path (redeem, explicit sign-in) never touches this counter and is correctly
 * left alone; it is not a cold-load reload and must not trigger a second, redundant status read
 * right after sign-in (the ordinary in-app-navigation case this check has always needed to skip).
 */
let lastAccountedRefreshCompletionCount = 0;

/** Test-only reset of {@link lastAccountedRefreshCompletionCount}, same convention as
 * `__resetRefreshCompletionCountForTest` (portal-auth/authenticated-fetch.ts) -- a real page load
 * starts this at 0 for free; a test harness sharing one module instance across `it()` blocks does
 * not. Callers that reset this should also reset the refresh-completion counter itself, so neither
 * side of the comparison carries state from an earlier, unrelated test. */
export function __resetColdLoadRefreshAccountingForTests(): void {
  lastAccountedRefreshCompletionCount = 0;
}

function RequireSession({ children }: Readonly<{ children: ReactNode }>) {
  const mountClaim = useSessionClaim();
  const navigate = useNavigate();
  const [needsColdLoadCheck] = useState(() => {
    if (mountClaim === null || mountClaim === undefined) {
      return true;
    }
    // Deliberately only READS the live count here, never commits it -- committing happens in the
    // bootstrap effect below, once this guard's own resolution (including any refresh IT triggers)
    // is known. Committing here instead would under-credit a refresh this same mount is about to
    // trigger, and -- critically -- would falsely flag the NEXT guard mount this one's own
    // "submitted" redirect produces (`/renewal-status` is itself `requireSession`-wrapped) as
    // needing a second, redundant cold-load check for a refresh that mount had nothing to do with.
    return getRefreshCompletionCount() > lastAccountedRefreshCompletionCount;
  });
  const [bootstrappedClaim, setBootstrappedClaim] = useState<
    { ready: false } | { ready: true; claim: ReturnType<typeof getCurrentRawClaim> }
  >(() => (!needsColdLoadCheck && mountClaim !== null && mountClaim !== undefined ? { ready: true, claim: mountClaim } : { ready: false }));
  // ADR-20261022-avfs ("status read on load"): checked ONLY when this mount needs a cold-load
  // check (a full page reload, a brand-new tab, or a step route whose own guard loader raced a
  // refresh in ahead of this mount -- see `lastAccountedRefreshCompletionCount`'s own doc comment
  // for why token presence at mount is not a sufficient test on its own), never on an ordinary
  // in-app navigation within an already-warm session -- a later mount's own useState initializer
  // above finds no unaccounted refresh and starts this at "not-needed", and the bootstrap effect's
  // own early-return (mirroring `bootstrappedClaim.ready` above) never touches it again. "After
  // submit" is a SEPARATE read, owned by `DeclarationRouteWrapper.attemptSubmit` itself, not this
  // one.
  const [submissionCheck, setSubmissionCheck] = useState<ColdLoadSubmissionCheck>(() =>
    needsColdLoadCheck ? "loading" : "not-needed",
  );

  useEffect(() => {
    if (bootstrappedClaim.ready) {
      return;
    }
    let cancelled = false;
    async function run() {
      // A step-route guard loader may already have raced a refresh in before this mount (see this
      // function's own doc comment) -- only fall back to `bootstrapSessionFromRefreshCookie` when
      // there is genuinely no claim yet; otherwise read the claim the loader already minted.
      let claim = getCurrentRawClaim();
      if (claim === null || claim === undefined) {
        await bootstrapSessionFromRefreshCookie();
        claim = getCurrentRawClaim();
      }
      // Commit the refresh count THIS guard's own resolution has now accounted for -- covers a
      // refresh this effect itself just triggered above, and one a step-route guard loader already
      // triggered before this mount (the case `needsColdLoadCheck`'s own init read caught). Doing
      // this here, rather than at init, means the account is up to date BEFORE any further guarded
      // mount this guard's own render produces (notably `/renewal-status`'s own `requireSession`
      // wrapper after a "submitted" redirect) -- see `needsColdLoadCheck`'s own doc comment.
      lastAccountedRefreshCompletionCount = getRefreshCompletionCount();
      if (cancelled) {
        return;
      }
      setBootstrappedClaim({ ready: true, claim });
      if (claim === null || claim === undefined) {
        // RequireSession's own render below sends a claimless mount to
        // /sign-in -- nothing to check submission status for.
        setSubmissionCheck("not-needed");
        return;
      }
      const outcome = await readSubmissionStatus();
      if (cancelled) {
        return;
      }
      if (outcome.status !== "Succeeded") {
        setSubmissionCheck("failed");
        return;
      }
      setSubmissionCheck(outcome.value.submitted ? "submitted" : "not-submitted");
    }
    void run();
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only -- a single bootstrap attempt per mount of
    // this guard, never re-triggered by a later render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function retrySubmissionCheck() {
    setSubmissionCheck("loading");
    void readSubmissionStatus().then((outcome) => {
      if (outcome.status !== "Succeeded") {
        setSubmissionCheck("failed");
        return;
      }
      setSubmissionCheck(outcome.value.submitted ? "submitted" : "not-submitted");
    });
  }

  if (!bootstrappedClaim.ready || submissionCheck === "loading") {
    return <PendingShell />;
  }
  if (bootstrappedClaim.claim === null || bootstrappedClaim.claim === undefined) {
    return <Navigate to="/sign-in" replace />;
  }
  if (submissionCheck === "failed") {
    // AC-7/AC-9: a failed status read on load shows the reload-failure view with retry -- never
    // section list or form.
    return <InlineReloadFailure onRetry={retrySubmissionCheck} onBackToRenewal={() => navigate("/")} />;
  }
  // OBJ-C-2/OBJ-C-3: `submissionCheck` only ever reaches "submitted" through the cold-load
  // bootstrap above (a genuine full page load/reload -- see that effect's own docstring), never
  // through an in-app navigation; the architecture plan's own AC-7 wording ("submitted routes
  // every path except /renewal/received AFTER A SUCCESS IN THIS TAB to the renewal-status view")
  // therefore can never legitimately except /renewal/received here -- "in this tab" success is
  // exactly the in-memory JS state a cold load has already lost by the time this check runs, for
  // the submitter's own reload as much as for anyone else's. Screen 7 stays reachable the one way
  // AC-6 actually grants it: the in-app navigation `attemptSubmit`'s own outcome handling performs
  // immediately after a successful write, which never touches this cold-load check at all.
  if (submissionCheck === "submitted") {
    return <Navigate to="/renewal-status" replace />;
  }
  return <CapabilityProvider claim={bootstrappedClaim.claim}>{children}</CapabilityProvider>;
}

/** Wraps a route `Component` with {@link RequireSession} -- used for every
 * claim-bearing route in the router table below, including the per-step
 * routes `buildStepRoutes` derives from `STEP_TABLE`. */
function requireSession<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  return function SessionGuarded(props: P) {
    return (
      <RequireSession>
        <Component {...props} />
      </RequireSession>
    );
  };
}

/**
 * AC-23: every journey route renders screen 8 instead of its own content
 * while the renewal window is closed -- "before it opens, screen 8 is the
 * only screen a signed-in customer can reach" (Business Rules). Reads
 * `isRenewalWindowOpen`/`renewalWindowOpensOn` from the SAME
 * `captureState.summary` every other route already reads through
 * `useAppData()` -- no second network read, and no date arithmetic of its
 * own (the port is the one source of both facts). While the summary read
 * has not yet settled (`"loading"`/`"failed"`), this renders `children`
 * unchanged so each route's own loading/failure handling still applies —
 * only a confirmed `isRenewalWindowOpen === false` ever substitutes screen 8.
 */
function RequireRenewalWindowOpen({ children }: Readonly<{ children: ReactNode }>) {
  const { captureState } = useAppData();
  if (captureState.status === "ready" && !captureState.summary.isRenewalWindowOpen) {
    return (
      <RenewalPreviewRoute
        businessName={captureState.summary.businessName}
        renewalDate={captureState.summary.renewalDate}
        policiesInScope={captureState.summary.policiesInScope}
        windowOpensOn={captureState.summary.renewalWindowOpensOn}
      />
    );
  }
  return <>{children}</>;
}

/** Wraps a route `Component` with both {@link RequireSession} and
 * {@link RequireRenewalWindowOpen} -- every journey route ("/" and every
 * per-step route) needs both guards; `/renewal-status` and
 * `/renewal/not-permitted` are terminal/edge states outside the journey
 * AC-23 names and keep `requireSession` alone. */
function requireSessionAndWindowOpen<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  return requireSession(function WindowGuarded(props: P) {
    return (
      <RequireRenewalWindowOpen>
        <Component {...props} />
      </RequireRenewalWindowOpen>
    );
  });
}

/**
 * m1-portal-ux-fixes Fix D (DEBT-20261023-sev7): confirm-details, exposures
 * and declaration are editable routes -- once this session has seen the
 * renewal submitted (`AppDataContextValue.isRenewalSubmitted`, set by a
 * Succeeded declaration submit or any write refused because the renewal is
 * already submitted), an IN-APP navigation back to one of them must land on
 * `/renewal-status` instead, the same outcome `RequireSession`'s own
 * cold-load bootstrap check already gives a full page reload -- see that
 * check's own doc comment on why it cannot cover an in-app navigation.
 * Deliberately a synchronous ref read, never a fresh `readSubmissionStatus`
 * call: that flag is already known true for the rest of this session once
 * set, so a network round trip on every single navigation to these three
 * routes would answer a question this session has already answered. Never
 * applied to "/" (landing) or `/renewal-status` itself -- neither is named
 * by this fix, and both already have their own, different post-submission
 * treatment (the landing route's own primary action, `/renewal-status`
 * being the destination).
 */
function requireNotSubmitted<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  return function NotSubmittedGuarded(props: P) {
    const { isRenewalSubmitted } = useAppData();
    if (isRenewalSubmitted()) {
      return <Navigate to="/renewal-status" replace />;
    }
    return <Component {...props} />;
  };
}

type CaptureState =
  | { status: "loading" }
  | { status: "failed" }
  | {
      status: "ready";
      renewalData: RenewalData;
      confirmedAnswers: ConfirmedAnswers;
      summary: RenewalSummaryReadResult;
      /**
       * OBJ-75/FIND-78: a reload requested from an ALREADY-`"ready"`
       * snapshot (`onSectionConfirmed`'s "read the server's own state
       * back" reload, after a write that itself succeeded) previously
       * replaced that valid snapshot outright with `{status:"failed"}`
       * on any failed re-read — including the ordinary single-5xx/
       * timeout case, not only an outage. The step route then read
       * `"failed"` and navigated away to `"/"`, where the landing
       * route's own, separately-fresh read succeeds and renders the
       * ordinary not-started/partially-complete state: a failed read
       * rendered as exactly the state AC-9's negative scenario forbids,
       * with the customer silently ejected from the step they were on.
       * Keeping the last good snapshot and flagging it `reloadFailed`
       * instead lets the step route stay mounted and show AC-9's stated
       * failure IN PLACE, with a control that retries the same reload —
       * never a redirect for this case, and never a second failure
       * surface: the landing route still owns the *initial*-read failure
       * (`{status:"failed"}` below, untouched).
       */
      reloadFailed?: boolean;
      /**
       * OBJ-138/FIND-203: which `reloadToken` this snapshot's DATA
       * actually reflects — set only when the async read below truly
       * lands (the final success `setState`), never by the synchronous
       * `reloadFailed` pre-clear above, which reuses `previous`'s
       * `generation` untouched via the spread. `useAdvanceOnReload` arms
       * against the generation current at arm time and waits for this
       * field to move past it, rather than firing on any `captureState`
       * change — the pre-clear IS such a change (a new object, with
       * `reloadFailed` flipped) despite carrying the same stale data the
       * armed confirm already saw, and firing on it consumed the armed
       * advance before the retried read's own data had landed.
       */
      generation: number;
    };

/** ADR-20261018-1skz: whether `portal-auth/token.ts` currently holds a
 * session token, read reactively via `useSyncExternalStore` so a caller
 * re-renders the moment sign-in, redeem, refresh or `RequireSession`'s
 * cold-load bootstrap sets (or clears) the token -- rather than a mount-
 * time snapshot, which is what `useSessionClaim` above deliberately keeps
 * (see its own doc comment for why a route-scoped claim stays snapshot-
 * only). This hook backs `useCaptureState`'s session gate below, which is
 * unrelated to the per-route claim and must react to the token appearing
 * on an UNMOUNTED route (the App-root composition this hook lives in is
 * mounted once, above the router). */
function useHasSession(): boolean {
  return useSyncExternalStore(subscribeTokenChange, () => getSessionToken() !== null);
}

/** Mirrors `test/support/renewal-capture-harness.tsx`'s own `useCaptureState` —
 * the same real composition that test harness documents itself as
 * standing in for, ahead of this file existing. Re-runs on `reloadToken`
 * change, which a step route bumps once a section confirmation succeeds,
 * so the next render reflects the server's own state rather than an
 * optimistic local echo (product-spec.md Business Rules: "resume reads
 * from the server, never from the browser").
 *
 * ADR-20261018-1skz: also gated on `hasSession` -- TG-4's swap from the
 * in-memory stub to `createHttpRenewalSummaryPort()` meant this effect,
 * which runs at the App-root composition unconditionally on every route
 * including `/sign-in` and `/redeem`, started making a real, unauthenticated
 * `readSummary`/`readConfirmedSections` call on those routes. That call
 * 401s, the reactive refresh also 401s with no refresh cookie, and the
 * registered session-ended handler navigates to `/sign-in/link-unavailable`
 * -- bouncing a customer who has not even reached sign-in yet. While
 * `hasSession` is `false` this effect makes neither call and leaves the
 * state exactly as it was (`"loading"` on first mount); it runs for the
 * first time the moment `hasSession` turns `true` (either a fresh sign-in/
 * redeem, or `RequireSession`'s cold-load bootstrap resolving a live
 * refresh cookie into a token) and keeps re-running on every later
 * `reloadToken` bump exactly as before. */
function useCaptureState(
  renewalSummaryPort: RenewalSummaryPort,
  workingCopyPort: WorkingCopyPort,
  reloadToken: number,
  hasSession: boolean,
): readonly [CaptureState, (result: LandingRetryResult) => void] {
  const [state, setState] = useState<CaptureState>({ status: "loading" });

  useEffect(() => {
    if (!hasSession) {
      return;
    }
    let stale = false;
    // OBJ-69/FIND-70 (judge turn 2, reopened): a STALE `"failed"` snapshot
    // from an earlier read must not survive into this reload — the guard
    // loader's own independent read (loadGuardContext) starts synchronously
    // as part of the same navigation and can resolve before this effect's
    // async read below settles, so a step route consuming a still-latched
    // `"failed"` bounces the customer straight back to "/" even though the
    // guard just allowed the navigation. Clearing to `"loading"` here,
    // synchronously at the top of the effect (before the awaited read),
    // means the step route renders the pending shell rather than the stale
    // failure while this reload's own read is in flight. A `"loading"`
    // snapshot is left untouched — only a stale `"failed"` is ever cleared
    // this way, and only in response to `reloadToken` changing, never
    // spontaneously.
    //
    // OBJ-137/FIND-201: a stale `reloadFailed: true` on an otherwise
    // `"ready"` snapshot gets the identical treatment, for the identical
    // reason — this flag, too, is App-wide rather than per-route and
    // survives both unmounting the route that raised it and a navigation
    // onto a DIFFERENT route (Save and finish later -> landing's own fresh
    // read succeeds -> primary action requests a reload and navigates in
    // the same tick -> the next route's guard admits it on its own fresh
    // read -> the next route mounts here against the still-`reloadFailed`
    // snapshot while THIS reload is in flight, rendering the AC-9
    // stated-failure surface for a read that has not failed at all).
    // Clearing it synchronously, the moment a new reload starts, means a
    // fresh route mount (or the landing page's own primary action) renders
    // the ordinary ready state — disabled fields are not needed, since the
    // data shown is still the last good snapshot — rather than the failed-
    // read surface, while this reload's own read is in flight. If this
    // reload itself fails, the branches below re-raise `reloadFailed: true`
    // exactly as before, so a GENUINE failure is unaffected: the surface
    // this clears is only ever the stale flag from a PRIOR reload, never
    // this one's own outcome.
    void (async () => {
      setState((previous) => {
        if (previous.status === "failed") {
          return { status: "loading" };
        }
        if (previous.status === "ready" && previous.reloadFailed === true) {
          return { ...previous, reloadFailed: false };
        }
        return previous;
      });
      let summaryOutcome: Awaited<ReturnType<RenewalSummaryPort["readSummary"]>>;
      let workingCopyOutcome: Awaited<ReturnType<WorkingCopyPort["readConfirmedSections"]>>;
      try {
        [summaryOutcome, workingCopyOutcome] = await Promise.all([
          renewalSummaryPort.readSummary(RENEWAL_ID),
          workingCopyPort.readConfirmedSections(RENEWAL_ID),
        ]);
      } catch {
        // OBJ-79/FIND-84, OBJ-81/FIND-86: a rejecting port promise (a
        // fetch-backed port's network-drop TypeError, the same failure
        // class FIND-35/OBJ-20 established for RenewalLanding.resolveNextState
        // and saveSection) must land in the SAME branch a returned
        // `{status:"Failed"}` outcome takes below — never escape as an
        // unhandled rejection, which would leave `captureState` latched at
        // its previous value (a permanent PendingShell on first load, or a
        // silently stale "ready" snapshot on a reload) with no stated
        // failure and no retry, violating AC-9's negative scenario.
        if (stale) {
          return;
        }
        setState((previous) =>
          previous.status === "ready" ? { ...previous, reloadFailed: true } : { status: "failed" },
        );
        return;
      }
      if (stale) {
        return;
      }
      if (summaryOutcome.status === "Failed" || workingCopyOutcome.status === "Failed") {
        // OBJ-75/FIND-78: only ever LATCH a full `"failed"` snapshot when
        // there was no good snapshot to fall back on (the initial read,
        // or a reload that was itself already stale/failed). A reload
        // that fails from an already-`"ready"` snapshot keeps that
        // snapshot's data and is flagged `reloadFailed` instead, so the
        // step route the customer is already on can show the stated
        // failure and retry in place rather than being unmounted.
        setState((previous) =>
          previous.status === "ready" ? { ...previous, reloadFailed: true } : { status: "failed" },
        );
        return;
      }
      setState({
        status: "ready",
        confirmedAnswers: toConfirmedAnswers(workingCopyOutcome.value.confirmedSections),
        renewalData: toRenewalData(summaryOutcome.value.policiesInScope),
        summary: summaryOutcome.value,
        // OBJ-138/FIND-203: `reloadToken` is this effect's own closed-over
        // value — stamping it here, only on a read that truly landed,
        // is what lets `useAdvanceOnReload` tell this apart from the
        // synchronous `reloadFailed` pre-clear above (which never touches
        // `generation`, via the `{...previous, ...}` spread).
        generation: reloadToken,
      });
    })();
    return () => {
      stale = true;
    };
  }, [renewalSummaryPort, workingCopyPort, reloadToken, hasSession]);

  // OBJ-C-20/FIND-863/867: applies a read `RenewalLanding` ALREADY made
  // (its own retry) to this state directly, rather than this hook
  // starting a second, fully redundant `readSummary`/
  // `readConfirmedSections` round trip of its own via `reloadToken`. Same
  // branches the effect above takes on landing, just fed from an outcome
  // already in hand instead of one freshly read here. Never bumps
  // `reloadToken`/`generation` — this applies the CURRENT reload's data,
  // it does not start a new one.
  const applyLandingReload = useCallback(
    (result: LandingRetryResult) => {
      if (result.phase === "failed") {
        setState((previous) =>
          previous.status === "ready" ? { ...previous, reloadFailed: true } : { status: "failed" },
        );
        return;
      }
      setState({
        status: "ready",
        confirmedAnswers: result.confirmedAnswers,
        renewalData: toRenewalData(result.summary.policiesInScope),
        summary: result.summary,
        generation: reloadToken,
      });
    },
    [reloadToken],
  );

  return [state, applyLandingReload] as const;
}

interface AppDataContextValue {
  readonly renewalSummaryPort: RenewalSummaryPort;
  readonly workingCopyPort: WorkingCopyPort;
  /** m1-exposure-detail-sourcing TG-6: screen 3's own account-record read
   * (AC-1/AC-11) -- the only consumer of this port today is
   * `ConfirmDetailsRouteWrapper`, below. */
  readonly renewalDataPort: RenewalDataPort;
  /** TG-5 (m1-exposure-detail-sourcing): the exposures step's own
   * data-on-file read -- the first real consumer of the port TG-4 of
   * this same feature already constructed below (`ExposuresJourneyRoute`'s
   * own `useOnFileRead`, called once per journey mount). */
  readonly onFilePort: OnFilePort;
  readonly declarationSubmissionPort: DeclarationSubmissionPort;
  readonly captureState: CaptureState;
  readonly requestReload: () => void;
  /** OBJ-C-20/FIND-863/867: applies a read `RenewalLanding`'s own retry
   * already made to `captureState` directly -- the one-read replacement
   * for `onReadRetried={requestReload}`, which issued a second, fully
   * redundant read of its own. See `useCaptureState`'s own doc comment. */
  readonly applyLandingReload: (result: LandingRetryResult) => void;
  /** Bridges a step's own real completion signal into the router's guard
   * context — see `router.tsx`'s `StepGuardContext.settledBeyondTarget`
   * doc comment for the "KNOWN GAP" this closes for exactly one step
   * (declaration) once it has been submitted in THIS session. Never a
   * step-id literal at the call site (below): always the `StepId` value
   * `resolveResumeTarget` itself just resolved. */
  readonly markStepSettled: (stepId: StepId) => void;
  /** OBJ-120/FIND-175: the read side of `markStepSettled`'s latch, for
   * `RenewalLanding`'s stepper (TG-17/AC-1) — the exact same
   * `StepId`-identity comparison `router.tsx`'s guard applies to the
   * same ref (OBJ-36: `context.settledBeyondTarget === redirectStep.id`),
   * so the stepper can never disagree with what the guard has already
   * admitted in this session. Never re-derived from anything else. */
  readonly isStepSettled: (stepId: StepId) => boolean;
  /** OBJ-104/FIND-146: `markStepSettled`'s claim certifies "the information
   * provided" (declaration/content.ts) at the moment of submission — it
   * says nothing about whatever the customer goes on to change afterwards.
   * A step route calls this, instead of `requestReload` directly, every
   * time one of ITS OWN section writes actually succeeds, so the claim is
   * invalidated on the only signal this file has for "the working copy the
   * declaration certified has since changed" (AC-4's own "Succeeded"
   * outcome — never a mere retry of a failed read, which is what
   * `requestReload`/`onRetry` are for elsewhere in this file and change
   * nothing about the working copy). Never keyed to which section changed:
   * ANY settled write, confirm-details/change-check or exposures alike,
   * can alter what "the information provided" at declaration time meant,
   * so the safe default is to require a fresh declaration after any of
   * them, not to guess which ones matter. */
  readonly onWorkingCopyWriteSucceeded: () => void;
  /** OBJ-123/FIND-181 (TG-18, ADR-20261001-rz8p): records the
   * `targetSectionId` half of `resolveBackNavigationTarget`'s result for
   * the step a "Back" control is about to navigate TO, so that step's own
   * route component can seed its default-expanded section with it on
   * arrival. Set immediately before the matching `navigate()` call and
   * consumed exactly once by `consumePendingBackExpand` — never carried as
   * a `navigate()`/`<Link>` `state` option or `history.pushState`/
   * `replaceState` payload, both eslint-banned (FIND-110/OBJ-92,
   * FIND-147/OBJ-105, AC-3) because a browser persists either to disk in
   * the session-history entry AC-3's capture chain does not observe. This
   * is ordinary in-memory React state, written and read only by this
   * same running app instance — nothing a browser or this repo's storage-
   * safety gates ever see. */
  readonly setPendingBackExpand: (target: { stepId: StepId; sectionId: SectionId } | undefined) => void;
  /** The read-and-clear side of `setPendingBackExpand`: returns the
   * pending section id ONLY when it was recorded for this exact `stepId`,
   * and clears it either way so it is never reused by a later, unrelated
   * arrival at the same step. */
  readonly consumePendingBackExpand: (stepId: StepId) => SectionId | undefined;
  /**
   * m1-portal-ux-fixes Fix D (DEBT-20261023-sev7): records, for the REST OF
   * THIS SESSION, that the renewal has been submitted -- either a real
   * submit succeeding (`DeclarationRouteWrapper`) or ANY write anywhere in
   * the capture journey being refused with a write-refusal
   * `isRenewalSubmittedWriteRefusal` recognises (`ConfirmDetailsRouteWrapper`,
   * `makeMultiSectionRoute`'s exposures route). A plain ref, exactly like
   * `markStepSettled`'s own `settledBeyondTargetRef` just above -- this
   * never needs to trigger a re-render on its own; it is only ever read at
   * the top of a route's own render (`isRenewalSubmitted`, a guard
   * component below), which already re-renders on every navigation.
   */
  readonly markRenewalSubmitted: () => void;
  /**
   * The read side of `markRenewalSubmitted` — `RequireNotSubmitted` below
   * (confirm-details/exposures/declaration only, never landing or
   * `/renewal-status` itself) reads this on every render and redirects to
   * `/renewal-status` when it is `true`, covering the in-app-navigation gap
   * `RequireSession`'s own cold-load-only submission check (its own doc
   * comment: "never through an in-app navigation") leaves open. Reusing
   * `readSubmissionStatus` here instead would add a network round trip to
   * EVERY navigation to one of these three routes; this flag is set
   * exactly when this session has already seen a submitted outcome (a
   * Succeeded submit, or a write refused because the renewal is already
   * submitted), so no fresh read is needed to answer the same question
   * again within the same session.
   */
  readonly isRenewalSubmitted: () => boolean;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

function useAppData(): AppDataContextValue {
  const value = useContext(AppDataContext);
  if (value === null) {
    throw new Error("App.tsx: a route rendered outside AppDataContext.Provider");
  }
  return value;
}

/** design-system-only pending shell for a step route whose own capture
 * read has not resolved yet (a direct deep-link on first load, before
 * this component's own effect settles) — mirrors router.tsx's identical
 * `RouteLoadingShell` text for its guard loader's own pending state. */
function PendingShell() {
  return (
    <PortalLayout>
      <Container>
        <Typography variant="body">{pendingShellCopy.loadingLabel}</Typography>
      </Container>
    </PortalLayout>
  );
}

/** OBJ-75/FIND-78: shown IN PLACE on a step route whose data is stale
 * because the reload that followed a (successful) write itself failed —
 * distinct from `PendingShell` (never seen a read succeed yet) and from
 * the landing route's own failed-read surface (the INITIAL read never
 * having succeeded at all). `onRetry` is always the same `requestReload`
 * every step route already depends on via `onSectionConfirmed` — never a
 * second reload mechanism invented here.
 *
 * OBJ-135/FIND-198: `onBackToRenewal` is the control the content fix added
 * `reloadFailureCopy.backToRenewalLabel` for — a failed read here leaves the
 * customer stranded with only a retry if the retry itself keeps failing, so
 * it follows the same callback-prop pattern as `ChangeCheckRoute`'s/
 * `DeclarationRoute`'s own `onBack` (a plain `() => void` signal, never a
 * path or router import of this component's own) — every call site below
 * supplies `() => navigate("/")`. */
function InlineReloadFailure({
  onRetry,
  onBackToRenewal,
}: {
  readonly onRetry: () => void;
  readonly onBackToRenewal: () => void;
}) {
  return (
    <PortalLayout>
      <Container>
        <PortalPanelBody id="capture-reload-failure" expanded tone="error" title={reloadFailureCopy.title} testId="capture-reload-failure">
          <Typography variant="body">{reloadFailureCopy.message}</Typography>
          <Button emphasis="primary" onClick={onRetry} testId="capture-reload-retry">
            {reloadFailureCopy.retryLabel}
          </Button>
          <Button
            emphasis="quiet"
            onClick={onBackToRenewal}
            startIcon={<Icon name="chevronLeft" />}
            testId="capture-reload-back-to-renewal"
          >
            {reloadFailureCopy.backToRenewalLabel}
          </Button>
        </PortalPanelBody>
      </Container>
    </PortalLayout>
  );
}

function LandingRoute() {
  const { renewalSummaryPort, workingCopyPort, requestReload, applyLandingReload, isStepSettled } = useAppData();
  const navigate = useNavigate();
  return (
    <RenewalLanding
      renewalId={RENEWAL_ID}
      renewalSummaryPort={renewalSummaryPort}
      workingCopyPort={workingCopyPort}
      isHostlessStepSettled={isStepSettled}
      // OBJ-C-18/FIND-851, revised for OBJ-C-20/FIND-863/867: this
      // component's own "Try again" retry re-runs only ITS OWN read
      // (above) -- without applying that outcome here, a first read that
      // failed latches `captureState` (this file's `useCaptureState`) at
      // `"failed"` and `RequireRenewalWindowOpen` never re-checks
      // `isRenewalWindowOpen` once this component's own retry succeeds,
      // so a window-closed customer whose first read hit a transient
      // error sees the ordinary landing instead of screen 8.
      // `applyLandingReload` (not `requestReload`) applies RenewalLanding's
      // own already-resolved outcome directly, rather than starting a
      // second, fully redundant read of its own -- one retry activation
      // now issues exactly one further GET.
      onReadRetried={applyLandingReload}
      onPrimaryAction={(target) => {
        // OBJ-69/FIND-70: RenewalLanding keeps its OWN read (and its own
        // "Try again" retry) entirely separate from this component's
        // `captureState`, which every step route below gates on. Without
        // this call, a `captureState.status === "failed"` latched by an
        // earlier read (App's own useCaptureState effect only re-runs on
        // `reloadToken` change) never clears just because RenewalLanding's
        // independent read has since succeeded — the customer reaches the
        // step route on a stale "failed" snapshot and is bounced straight
        // back to "/" by that route's own guard, even though the read that
        // resolved `target` here (and the router's own, separately-fresh
        // `loadGuardContext`) just succeeded. Requesting a reload here,
        // right where the customer's own successful read is already in
        // hand, means the step route mounts against a fresh read of its
        // own rather than a possibly-stale one — never a second read this
        // file invents, just the same reload token every step route
        // already depends on via `onSectionConfirmed`.
        requestReload();
        const step = STEP_TABLE.find((candidate) => candidate.id === target.targetStep);
        if (step !== undefined) {
          void navigate(step.path);
        }
      }}
    />
  );
}

/**
 * TG-18: the one place a step route's "reload-in-progress-failed" banner
 * is built. Extracted here so `ConfirmDetailsRouteWrapper`/
 * `ChangeCheckRouteWrapper` (dedicated, single-section routes) and
 * `makeMultiSectionRoute`'s own renderer (still serving `exposures`)
 * render the identical OBJ-112/OBJ-114/OBJ-116 surface from one
 * definition rather than three drifting copies of the same JSX.
 */
function buildReloadFailureBanner(
  captureState: Extract<CaptureState, { status: "ready" }>,
  requestReload: () => void,
): ReactNode {
  if (captureState.reloadFailed !== true) {
    return null;
  }
  return (
    <PortalPanelBody id="capture-reload-failure-banner" expanded tone="error" title={reloadFailureCopy.title} testId="capture-reload-failure-banner">
      <Typography variant="body">{reloadFailureCopy.message}</Typography>
      <Button emphasis="primary" onClick={requestReload} testId="capture-reload-retry">
        {reloadFailureCopy.retryLabel}
      </Button>
    </PortalPanelBody>
  );
}

/**
 * The step declared immediately after `stepId` in `STEP_TABLE`'s own
 * `order` field, or `undefined` if `stepId` is the last step or is not in
 * the table at all. No step-id literal beyond the one the caller supplies:
 * the one `order + 1` derivation, shared by `DeclarationRouteWrapper`'s
 * submit handler (`advanceToNextStep`) and a SECOND
 * route (`ConfirmDetailsRoute`'s Continue — TG-15/AC-8) rather
 * than re-deriving it, or worse, hardcoding the "change-check" path it
 * should always land on (OBJ-117/OBJ-119, FIND-170/FIND-172).
 */
function nextStepPath(stepId: StepId): string | undefined {
  const step = STEP_TABLE.find((candidate) => candidate.id === stepId);
  if (step === undefined) {
    return undefined;
  }
  return STEP_TABLE.find((candidate) => candidate.order === step.order + 1)?.path;
}

/**
 * TG-18: the shared "advance once the reload actually reflects the write"
 * mechanism for the two dedicated single-section routes this task group
 * adds (`ConfirmDetailsRoute`, `ChangeCheckRoute`). A section confirm's
 * own `onSectionConfirmed` calls the returned `arm()` and then the
 * ordinary `requestReload()` every step route already uses; this hook's
 * effect watches the SAME `captureState` every route already renders
 * against and, the moment it next settles `"ready"` with no
 * `reloadFailed` AND a `generation` past the one `arm()` saw (whether
 * that is the very next render or only after the customer retries a
 * reload that initially failed — OBJ-75/FIND-78's own shape, with
 * OBJ-138/FIND-203's generation check added so the retry's OWN landed
 * read is what fires this, not useCaptureState's synchronous
 * `reloadFailed` pre-clear on the still-stale snapshot), asks the
 * caller-supplied `resolveTargetPath` where to go and
 * navigates once, then clears the flag. No second, independent read:
 * unlike a one-shot fresh read taken at confirm time, this naturally also
 * fires after a RETRIED reload succeeds, because `captureState` changing
 * at all is what drives the effect.
 *
 * OBJ-117/OBJ-119 (FIND-170/FIND-172): `resolveTargetPath` is a parameter,
 * not a hardcoded call to `resolveResumeTarget`, because the two callers
 * genuinely want different things. `ChangeCheckRoute`'s Continue (TG-16)
 * IS a "where does the customer's answer send them" branch — exactly what
 * `resolveResumeTarget` (the shared "first open section, else the next
 * hostless step" computation) already answers correctly on every path,
 * first pass or revisit alike, because change-check's own answer is what
 * the fresh read just changed. `ConfirmDetailsRoute`'s Continue (TG-15,
 * AC-8) is NOT that question: AC-8 declares a fixed five-route order, and
 * "resume to wherever the first open thing is" silently skips change-check
 * on any revisit where change-check (or everything after it) is already
 * confirmed — the customer lands past a question AC-8's own Back controls
 * exist to let them return to. `nextStepPath` names the fix for that
 * caller: always the step declared immediately after confirm-details,
 * independent of section state.
 */
function useAdvanceOnReload(
  captureState: CaptureState,
  navigate: (path: string) => void,
  resolveTargetPath: (ready: Extract<CaptureState, { status: "ready" }>) => string | undefined,
) {
  const pendingAdvanceRef = useRef(false);
  // OBJ-138/FIND-203: the generation `arm()` saw at call time. The
  // reload this arms for is the one `requestReload()` kicks off in the
  // very same tick as `arm()` — `captureState.generation` has not moved
  // yet when `arm()` reads it, so this is always the PRE-reload
  // generation, whether this is the write's first reload or a customer
  // retrying one that failed.
  const armedGenerationRef = useRef<number | undefined>(undefined);

  const arm = useCallback(() => {
    pendingAdvanceRef.current = true;
    armedGenerationRef.current = captureState.status === "ready" ? captureState.generation : undefined;
  }, [captureState]);

  useEffect(() => {
    if (!pendingAdvanceRef.current) {
      return;
    }
    if (captureState.status !== "ready" || captureState.reloadFailed === true) {
      return;
    }
    // A `reloadFailed` snapshot getting synchronously cleared back to
    // `reloadFailed: false` (useCaptureState's OBJ-137 pre-clear, fired
    // the instant a NEW reload starts, before its own read has landed)
    // is a `captureState` change this effect would otherwise see and
    // treat as "the reload settled" — on the stale, pre-write data the
    // armed confirm already resolved against, consuming the arm before
    // the retried read's actual data ever arrives. The pre-clear never
    // touches `generation` (see its `{...previous, ...}` spread), so
    // comparing generations, not just reacting to any `captureState`
    // change, tells the two apart.
    if (captureState.generation === armedGenerationRef.current) {
      return;
    }
    pendingAdvanceRef.current = false;
    const targetPath = resolveTargetPath(captureState);
    if (targetPath !== undefined) {
      navigate(targetPath);
    }
  }, [captureState, navigate, resolveTargetPath]);

  return arm;
}

/**
 * TG-18 (ADR-20261001-rz8p, narrowed by its 2026-10-02 amendment for
 * OBJ-133/FIND-196): the composition-root-side half of the shared
 * back-navigation resolver. Computes section state from whichever
 * `renewalData`/`confirmedAnswers` the caller supplies, resolves it
 * through `resolveBackNavigationTarget`, and returns the resolved step's
 * own declared path — never a path this file constructs itself.
 * `undefined` means "no back control" (the resolver found no earlier step
 * at all).
 *
 * This function itself stays pure and does no I/O — the original doc
 * comment here claimed "no second fresh read ... a 'back' navigation is
 * not a write" and called the already-loaded `captureState` snapshot
 * sufficient. OBJ-133/FIND-196 found that wrong: a committed-but-
 * Indeterminate write (ADR-20260821-kp45) that this file deliberately does
 * not reload leaves that snapshot stale for exactly as long as it takes
 * the next reload to land, and a customer can reach a route and press
 * "Back" inside that window. `resolveFreshBackTarget` below is the
 * caller-side mechanism that now feeds this function a FRESH read instead
 * — this function's own contract (pure, caller supplies the data) is
 * unchanged.
 *
 * OBJ-123/FIND-181: also returns the resolver's own `targetSectionId`
 * (renamed `sectionId` here) rather than discarding it — the caller's
 * `onBack` records it via `setPendingBackExpand` immediately before
 * navigating, so the target step's own route can seed its default-
 * expanded section with "the last exposures section" the resolver's doc
 * comment already names, instead of landing on every section collapsed.
 */
function resolveBackTarget(
  stepId: StepId,
  renewalData: RenewalData,
  confirmedAnswers: ConfirmedAnswers,
): { readonly path: string; readonly stepId: StepId; readonly sectionId?: SectionId } | undefined {
  const sectionStates = computeSectionStates(SECTION_TABLE, STEP_TABLE, confirmedAnswers, renewalData);
  const target = resolveBackNavigationTarget(stepId, sectionStates, STEP_TABLE);
  if (target === undefined) {
    return undefined;
  }
  const path = STEP_TABLE.find((step) => step.id === target.targetStep)?.path;
  if (path === undefined) {
    return undefined;
  }
  return {
    path,
    stepId: target.targetStep,
    ...(target.targetSectionId !== undefined ? { sectionId: target.targetSectionId } : {}),
  };
}

/**
 * OBJ-133/FIND-196 (narrows ADR-20261001-rz8p, amended 2026-10-02): a
 * committed-but-Indeterminate write (ADR-20260821-kp45) that this file
 * deliberately does not reload (`onSectionWriteIndeterminate` — "minus the
 * reload") leaves `captureState` stale for exactly as long as it takes the
 * NEXT reload to land — long enough, per the adversary's reproduction, for
 * a customer to "Save and finish later", return via the landing route's
 * own independently-fresh primary action, and press "Back" on the route
 * that write landed them on before App's own reload has caught up.
 * `resolveBackTarget` computed from that stale snapshot can pick the wrong
 * earlier step — one whose sections are stale-reported "locked" rather
 * than the fresh-true "inapplicable" — so the destination route's own
 * guard loader redirects past it on arrival (ADR-20260918-dd9e): "Back"
 * becomes a silent no-op rather than wrong data being acted on, but AC-8's
 * declared back target is still not reached.
 *
 * This resolves the SAME `resolveBackTarget` decision from a FRESH read of
 * both ports — the identical mechanism `DeclarationRouteWrapper`'s own
 * `attemptSubmit` already uses for the same class of defect
 * (OBJ-109/FIND-154) — rather than a second, independent park/retry signal
 * from the guard loader (the alternative the human declined, in favour of
 * narrowing this ADR instead). Falls back to the caller's already-loaded
 * (possibly stale) snapshot only if the fresh read itself fails outright —
 * a plain read failure is not reason to leave "Back" inert, and the
 * destination's guard loader re-validates whatever this resolves to
 * regardless, exactly as it did before this fix.
 */
async function resolveFreshBackTarget(
  stepId: StepId,
  renewalSummaryPort: RenewalSummaryPort,
  workingCopyPort: WorkingCopyPort,
  fallback: { readonly renewalData: RenewalData; readonly confirmedAnswers: ConfirmedAnswers },
): Promise<ReturnType<typeof resolveBackTarget>> {
  try {
    const [summaryOutcome, workingCopyOutcome] = await Promise.all([
      renewalSummaryPort.readSummary(RENEWAL_ID),
      workingCopyPort.readConfirmedSections(RENEWAL_ID),
    ]);
    if (summaryOutcome.status === "Failed" || workingCopyOutcome.status === "Failed") {
      return resolveBackTarget(stepId, fallback.renewalData, fallback.confirmedAnswers);
    }
    return resolveBackTarget(
      stepId,
      toRenewalData(summaryOutcome.value.policiesInScope),
      toConfirmedAnswers(workingCopyOutcome.value.confirmedSections),
    );
  } catch {
    // FIND-156's own precedent (attemptSubmit): a rejecting port promise
    // lands in the same branch a returned `Failed` outcome takes above,
    // never an unhandled rejection that leaves "Back" silently inert.
    return resolveBackTarget(stepId, fallback.renewalData, fallback.confirmedAnswers);
  }
}

/**
 * m1-chase-and-send TG-4 (ADR-20261021-wzgk): tells the server the renewal's owner confirmed a
 * section, so the chase can send the not-started or the partially-complete reminder as the
 * customer's progress warrants. Called only from the owner's confirm sites below: the route is
 * owner-only server-side (a contributor is refused 403), so a contributor's confirm never calls it.
 *
 * Fire and forget. The working-copy write the customer just made IS the confirmation; this only
 * informs the chase, so it neither blocks the journey nor surfaces a failure to the customer
 * (`recordSectionConfirmation` never rejects, and the server is idempotent per renewal and
 * section). A repeated section is recorded as its section type, never its instance, which is all
 * the chase counts.
 */
function reportOwnerSectionConfirmed(sectionId: SectionInstanceId): void {
  void recordSectionConfirmation(decodeSectionInstanceId(sectionId).sectionId);
}

/**
 * TG-18 (ADR-20261001-rjj8): the dedicated confirm-details route's
 * composition-root wrapper, replacing this step id's former
 * `makeMultiSectionRoute("confirm-details")` entry. Mirrors that
 * renderer's own failed/loading/reload-failure branches exactly — the
 * only change is which presentational component renders once
 * `captureState` is ready, and that "Continue" now navigates on (the
 * split-route composition gap this task group exists to close).
 */
/**
 * m1-exposure-detail-sourcing AC-1/AC-9/AC-14: screen 3's own prefill
 * sources -- the stored account record and this section's own draft, if
 * "Save and finish later" was used on a prior visit -- read once per
 * mount, alongside (never instead of) the shared `captureState` read
 * every other step route already consumes. Neither source changes while
 * this route is mounted: the account record is never written by this
 * feature, and a draft is only ever replaced by THIS route's own
 * `writeDraftSection` call, which navigates away immediately after --
 * so, unlike `captureState`, this needs no `reloadToken` dependency, only
 * its own retry.
 */
type ConfirmDetailsScreenState =
  | { readonly status: "loading" }
  | { readonly status: "failed" }
  | {
      readonly status: "ready";
      readonly accountDefaults: AccountReadResult;
      readonly draftAnswers: Record<string, unknown> | undefined;
    };

function useConfirmDetailsScreenData(
  renewalDataPort: RenewalDataPort,
  workingCopyPort: WorkingCopyPort,
  enabled: boolean,
): readonly [ConfirmDetailsScreenState, () => void] {
  const [state, setState] = useState<ConfirmDetailsScreenState>({ status: "loading" });
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    // `enabled` is the owner-only capability gate (AC-19): a contributor is
    // refused `GET /api/portal/account` with 403, so the read never fires for
    // one -- `ConfirmDetailsRouteWrapper` shows the not-permitted view instead.
    if (!enabled) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      // Mirrors `useCaptureState`'s own OBJ-69 pre-clear, immediately above
      // in this file: a stale "failed" snapshot from an earlier read must
      // not survive into THIS reload (a retry re-arming the effect via
      // `retryToken`), so it clears synchronously, before the awaited read
      // below, via a functional update -- never an unconditional, plain-
      // object `setState` call, which would re-render every mount
      // (including the ordinary first one, already "loading") for no
      // reason.
      setState((previous) => (previous.status === "failed" ? { status: "loading" } : previous));
      let accountOutcome: Awaited<ReturnType<RenewalDataPort["readAccount"]>>;
      let draftOutcome: Awaited<ReturnType<NonNullable<WorkingCopyPort["readDraftSections"]>>> | undefined;
      try {
        [accountOutcome, draftOutcome] = await Promise.all([
          renewalDataPort.readAccount(RENEWAL_ID),
          workingCopyPort.readDraftSections?.(RENEWAL_ID),
        ]);
      } catch {
        // Mirrors `useCaptureState`'s own OBJ-79/OBJ-81 treatment: a
        // rejecting port promise (a network drop) is AC-9's stated
        // failure, never an unhandled rejection left to latch this
        // screen at "loading" forever.
        if (!cancelled) {
          setState({ status: "failed" });
        }
        return;
      }
      if (cancelled) {
        return;
      }
      if (accountOutcome.status === "Failed" || draftOutcome?.status === "Failed") {
        setState({ status: "failed" });
        return;
      }
      const draftSections = draftOutcome?.status === "Succeeded" ? draftOutcome.value.draftSections : [];
      const draft = draftSections.find((section) => section.sectionId === BUSINESS_DETAILS_SECTION_ID);
      setState({ status: "ready", accountDefaults: accountOutcome.value, draftAnswers: draft?.answers });
    })();
    return () => {
      cancelled = true;
    };
  }, [renewalDataPort, workingCopyPort, enabled, retryToken]);

  return [state, () => setRetryToken((count) => count + 1)];
}

function ConfirmDetailsRouteWrapper() {
  const { workingCopyPort, renewalDataPort, captureState, requestReload, onWorkingCopyWriteSucceeded, markRenewalSubmitted } =
    useAppData();
  const navigate = useNavigate();
  // OBJ-117/OBJ-119 (FIND-170/FIND-172): fixed target, never the shared
  // resume computation — see `useAdvanceOnReload`'s doc comment. AC-8
  // declares confirm-details is always followed by change-check; "resume
  // to the first open section" silently skips it on any revisit where
  // change-check (or everything after it) is already confirmed.
  const resolveConfirmDetailsTarget = useCallback(() => nextStepPath("confirm-details"), []);
  const armAdvance = useAdvanceOnReload(captureState, navigate, resolveConfirmDetailsTarget);
  const capability = useCapability();
  const [screenState, retryScreenData] = useConfirmDetailsScreenData(
    renewalDataPort,
    workingCopyPort,
    capability.canSubmitRenewal,
  );

  // AC-19 (ADR-20261015-c9zb): screen 3 is the owner's; a contributor who
  // requests it sees the not-permitted view.
  if (!capability.canSubmitRenewal) {
    return <NotPermittedPage onReturnToRenewal={() => navigate("/")} />;
  }

  // AC-9: a failed account/draft read is the identical stated-failure
  // surface a failed captureState read already shows on this screen --
  // never an empty prefilled form (product-spec AC-9's own "no screen
  // displays an empty prefilled form ... as the result of a failed
  // read"). Retry re-issues BOTH this screen's own read and the shared
  // captureState reload, since either one failing is this screen's own
  // failure.
  if (captureState.status === "failed" || screenState.status === "failed") {
    return (
      <InlineReloadFailure
        onRetry={() => {
          requestReload();
          retryScreenData();
        }}
        onBackToRenewal={() => navigate("/")}
      />
    );
  }
  if (captureState.status === "loading" || screenState.status === "loading") {
    return <PendingShell />;
  }

  return (
    <ConfirmDetailsRoute
      renewalId={RENEWAL_ID}
      stepperItems={atStage(
        buildStepperItems(captureState.confirmedAnswers, captureState.renewalData, navigate, ["confirm-details", "exposures"]),
        "confirm-details",
      )}
      businessName={captureState.summary.businessName}
      confirmedAnswers={captureState.confirmedAnswers}
      accountDefaults={screenState.accountDefaults}
      {...(screenState.draftAnswers !== undefined ? { draftAnswers: screenState.draftAnswers } : {})}
      port={workingCopyPort}
      reloadFailureBanner={buildReloadFailureBanner(captureState, requestReload)}
      onSectionConfirmed={(sectionId) => {
        // TG-4 (ADR-20261021-wzgk): screen 3 is the owner's alone (guarded
        // above), so every confirm here is the owner's.
        reportOwnerSectionConfirmed(sectionId);
        // OBJ-104/FIND-146: invalidate any existing declaration
        // certification BEFORE reloading or navigating — the identical
        // ordering every other write site in this file already follows.
        onWorkingCopyWriteSucceeded();
        armAdvance();
        requestReload();
      }}
      onSectionWriteIndeterminate={() => {
        onWorkingCopyWriteSucceeded();
      }}
      onRenewalSubmitted={() => {
        markRenewalSubmitted();
        navigate("/renewal-status");
      }}
      // AC-12: "Save and finish later" leads to screen 7's saved state,
      // not back to the landing route.
      onSaveAndFinishLater={() => navigate("/renewal/received?outcome=saved")}
    />
  );
}

/**
 * TG-5/AC-13 (m1-magic-link-portal-refresh): the "Has anything changed?"
 * step no longer exists — "every customer goes through the sections" (H2
 * README, Screen 3). `route-table.ts`'s change-check section is now
 * permanently inapplicable, so `STEP_ROUTE_COMPONENTS["change-check"]`'s
 * own `requireSessionAndWindowOpen`-wrapped router loader guard
 * (`router.tsx`'s `createStepGuardLoader`, via `step-guard.ts`'s "every
 * hosted section inapplicable" rule) already throws `redirect()` to the
 * earliest genuinely incomplete step before this component ever mounts —
 * see `routing/step-guard.test.ts`'s own suite for that guarantee. This
 * replaces the retired `ChangeCheckRouteWrapper`/`ChangeCheckRoute`
 * (TG-18's dedicated route, now deleted): a defensive redirect only,
 * never the primary mechanism, for the one case the loader guard does not
 * cover — a client-side render that reaches this component without a
 * fresh loader run (a test harness driving the component tree directly,
 * never a real browser navigation, which `createBrowserRouter` always
 * re-runs the loader for).
 */
function ChangeCheckRedirectRoute() {
  const { captureState } = useAppData();
  const navigate = useNavigate();

  useEffect(() => {
    if (captureState.status !== "ready") {
      return;
    }
    const sectionStates = computeSectionStates(
      SECTION_TABLE,
      STEP_TABLE,
      captureState.confirmedAnswers,
      captureState.renewalData,
    );
    const target = resolveResumeTarget(sectionStates, STEP_TABLE);
    const targetPath = STEP_TABLE.find((step) => step.id === target.targetStep)?.path ?? "/";
    void navigate(targetPath, { replace: true });
    // Re-runs only if the ready snapshot's own identity changes -- this
    // effect's whole job is "redirect once, as soon as data is ready".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureState.status]);

  return <PendingShell />;
}

/**
 * The H2 three-stage stepper's items for screens 4&5 and 6 (TG-6), from
 * the same shared `computeStepperStages` the landing route uses.
 */
/** H2 draws each screen's stepper statically: the stages before the screen's own are Completed,
 * its own is In progress and the rest To do (03, 04-05 and 06 screen files). ADR-20261102-h2fd. */
function atStage(items: readonly StepperItemData[], stageId: string): readonly StepperItemData[] {
  const index = items.findIndex((item) => item.id === stageId);
  return items.map((item, position) => {
    const status = position < index ? "done" : position === index ? "current" : "todo";
    return { ...item, status, statusText: getStepperStatusText(status) };
  });
}

function buildStepperItems(
  confirmedAnswers: ConfirmedAnswers,
  renewalData: RenewalData,
  navigate?: (path: string) => void,
  navigableStages: readonly string[] = [],
): readonly StepperItemData[] {
  const sectionStates = computeSectionStates(SECTION_TABLE, STEP_TABLE, confirmedAnswers, renewalData);
  const stepperStages = computeStepperStages(STEPPER_STAGE_TABLE, sectionStates, () => false);
  const stageDescriptorsById = new Map(STEPPER_STAGE_TABLE.map((stage) => [stage.id, stage]));
  return stepperStages.map((stage) => {
    let status: "current" | "done" | "todo";
    if (stage.state === "current") {
      status = "current";
    } else if (stage.state === "done") {
      status = "done";
    } else {
      status = "todo";
    }
    // ADR-20261102-h2fd (AC-15): the steps H2 makes buttons on each screen go to that step.
    const onSelect =
      navigate !== undefined && navigableStages.includes(stage.stageId) ? () => navigate(`/renewal/${stage.stageId}`) : undefined;
    return {
      id: stage.stageId,
      label: getStageLabel(stageDescriptorsById.get(stage.stageId)?.labelKey ?? ""),
      status,
      statusText: getStepperStatusText(stage.state),
      ...(onSelect !== undefined ? { onSelect } : {}),
    };
  });
}

function makeMultiSectionRoute(stepId: "exposures") {
  return function StepRoute() {
    const { workingCopyPort, onFilePort, captureState, requestReload, onWorkingCopyWriteSucceeded, markRenewalSubmitted } =
      useAppData();
    const navigate = useNavigate();
    const capability = useCapability();
    // OBJ-123/FIND-181's "seed the last exposures section" mechanism
    // (`consumePendingBackExpand`) applied to `MultiSectionStepRoute`'s
    // own accordion, which this task group retires for the exposures step
    // (`ExposuresJourneyRoute` below). TG-6's own H2 screens 4&5 always
    // land on the section LIST on arrival here, including a genuine
    // "Back" navigation from declaration — never deep-linked to a
    // specific section — so this mechanism is intentionally not carried
    // forward for this step; tracked as DEBT-20261015-exq3.
    if (captureState.status === "failed") {
      // OBJ-77/FIND-80: the router's own step-order guard did its own,
      // independent read (loadGuardContext, below) to decide whether this
      // navigation was even allowed — that read can succeed while THIS
      // component's own separate read (never shared, per this file's doc
      // comment) fails, on a deep link / refresh where no prior "ready"
      // snapshot exists yet. Redirecting to "/" here relied on the landing
      // route's own, later, independent read failing too — it does not:
      // a single transient failure on this read leaves the landing route's
      // read to succeed on its own and render the ordinary not-started/
      // partially-complete state, exactly the shape AC-9's negative
      // scenario forbids. Showing the same in-place failure surface as the
      // `reloadFailed` case below, regardless of whether this is the
      // initial read or a reload, means a failed read is never silently
      // presented as an ordinary one.
      return <InlineReloadFailure onRetry={requestReload} onBackToRenewal={() => navigate("/")} />;
    }
    if (captureState.status === "loading") {
      return <PendingShell />;
    }

    // OBJ-112/FIND-160: a `reloadFailed` reload is requested FROM this
    // already-mounted route (see useCaptureState: `onSectionConfirmed`'s
    // own post-write re-read), so the customer may have in-progress,
    // unconfirmed edits in `MultiSectionStepRoute`'s own component state
    // (a reopened section's field values, among others) at the moment it
    // fires. Replacing this component with `InlineReloadFailure` — OBJ-75's
    // original fix — stopped the false "ordinary state" render but
    // unmounts `MultiSectionStepRoute` regardless, destroying exactly that
    // in-progress state even though AC-4 commits only to the customer's
    // OWN leaving/reloading as the accepted loss, not a loss the portal
    // itself causes after a save attempt it did not ask the customer to
    // repeat. `captureState.renewalData`/`confirmedAnswers` stay the last
    // good snapshot specifically so this stays mountable (see the
    // `CaptureState` type's own doc comment) — render the failure Alert
    // ABOVE it, stale-but-valid, rather than instead of it, so retry never
    // requires the customer to reconstruct an edit that was never lost.
    // OBJ-135/FIND-198 (TG-5 content criterion -> AC-9): was a third
    // hand-typed copy of the identical Alert this function's own doc
    // comment on `buildReloadFailureBanner` already claims is extracted
    // "so these render from one definition rather than three drifting
    // copies" — it was not actually called here, so a developer editing
    // one copy would silently not touch the other two. `captureState` is
    // already narrowed to "ready" by the two returns above, matching
    // `buildReloadFailureBanner`'s own parameter type exactly.
    const reloadFailureBanner = buildReloadFailureBanner(captureState, requestReload);
    // ADR-20261015-c9zb: the owner capability (never a role) decides the
    // owner and contributor compositions.
    const isOwner = capability.canSubmitRenewal;
    // FIX-1 (m1-portal-design-fixes), H2 README.md:185-186: the list
    // view's own "Back to confirm details" link always targets the
    // confirm-details STEP route itself -- never the generic
    // resolveBackTarget computation `DeclarationRoute`'s own back control
    // uses, which would land on "change-check" were that step not
    // permanently retired (`changeCheckAllowsExposures`, route-table.ts).
    // STEP_TABLE is the one place this path lives (route-table.ts's own
    // header comment).
    const confirmDetailsPath = STEP_TABLE.find((step) => step.id === "confirm-details")?.path;

    return (
      <ExposuresJourneyRoute
        renewalId={RENEWAL_ID}
        businessName={captureState.summary.businessName}
        policiesInScope={captureState.summary.policiesInScope}
        confirmedAnswers={captureState.confirmedAnswers}
        port={workingCopyPort}
        onFilePort={onFilePort}
        isOwner={isOwner}
        {...(isOwner
          ? {
              stepperItems: atStage(
                buildStepperItems(captureState.confirmedAnswers, captureState.renewalData, navigate, ["confirm-details"]),
                "exposures",
              ),
            }
          : { delegatedSectionIds: capability.confirmableSectionIds })}
        {...(isOwner && confirmDetailsPath !== undefined ? { onBack: () => navigate(confirmDetailsPath) } : {})}
        reloadFailureBanner={reloadFailureBanner}
        // Fix B (ADR-20261027-8fd1, owner decision
        // option B): `onSectionConfirmed` no longer arms a navigate to
        // declaration once every section settles -- that auto-advance fired
        // on EVERY confirm, so completing the last section, or re-confirming
        // any section after all were already settled, both yanked the
        // customer to declaration with no chance to review. An owner now
        // moves on only via "Continue and submit" below (shown by
        // `SectionListRoute` once every row is settled), which already
        // navigates explicitly.
        onSectionConfirmed={(sectionId) => {
          // TG-4 (ADR-20261021-wzgk): only the owner's confirm is the
          // chase's signal; the route refuses a contributor's.
          if (isOwner) {
            reportOwnerSectionConfirmed(sectionId);
          }
          // OBJ-104/FIND-146: a settled declaration claim certified the
          // working copy as it stood at submission time. THIS section's
          // write just succeeded against that same working copy, so
          // whatever the claim certified may no longer be current —
          // invalidate it before reloading, never after (a step route
          // reading a stale `settledBeyondTarget` between these two calls
          // is exactly the window OBJ-104 found).
          onWorkingCopyWriteSucceeded();
          requestReload();
        }}
        onSectionWriteIndeterminate={() => {
          // OBJ-106/FIND-150: an Indeterminate write may have committed
          // server-side even though the component couldn't confirm it —
          // the same claim-invalidating call as a Succeeded write, minus
          // the reload (there is no new `confirmedAnswers` value to read:
          // `onSectionConfirmed`'s own answers argument is never produced
          // for this outcome, so the only known-unchanged case left is
          // Failed).
          onWorkingCopyWriteSucceeded();
        }}
        // AC-15/AC-33: screen 7's saved state. ADR-20261016-q2mt lets the
        // step guard admit /renewal/received when ?outcome=saved is present.
        onSaveAndFinishLater={() => navigate("/renewal/received?outcome=saved")}
        onContinueAndSubmit={() => {
          const path = nextStepPath(stepId);
          if (path !== undefined) {
            void navigate(path);
          }
        }}
        // AC-19: screen 7's colleague-submitted state.
        onContributorSubmitted={() => navigate("/renewal/received")}
        onNotPermitted={() => navigate("/renewal/not-permitted")}
        onRenewalSubmitted={() => {
          markRenewalSubmitted();
          navigate("/renewal-status");
        }}
      />
    );
  };
}

/** ADR-20261022-cgx7: the section-classification half of `attemptSubmit`'s fresh read, pulled out
 * to a standalone function so its branching (per section: inapplicable-or-confirmed, then
 * completed-by-colleague, then not-completed) is assessed on its own rather than folded into
 * `DeclarationRouteWrapper`'s cognitive complexity. `sectionStates` is the instance-aware settled view
 * (DEBT-20261103-snze). */
function classifySectionCompletionStatuses(
  sectionStates: readonly SectionStateEntry[],
  confirmedAnswers: ConfirmedAnswers,
  completedByColleagueSectionIds: ReadonlySet<string>,
): SectionCompletionStatus[] {
  return SECTION_TABLE.map((section) => {
    const entry = sectionStates.find((candidate) => candidate.sectionId === section.id);
    const isInapplicable = entry?.state === "inapplicable";
    // DEBT-20261103-snze: a repeated section the owner settled instance by instance counts as
    // confirmed even before its section-type roll-up lands; a colleague's share takes precedence.
    const isConfirmed =
      isSectionConfirmed(confirmedAnswers, section.id) ||
      (entry?.state === "confirmed" && !completedByColleagueSectionIds.has(section.id));
    let state: SectionCompletionState;
    if (isInapplicable || isConfirmed) {
      state = "completed";
    } else if (completedByColleagueSectionIds.has(section.id)) {
      state = "completed-by-colleague";
    } else {
      state = "not-completed";
    }
    return { sectionId: section.id, state };
  });
}

/** The outcome of `loadFreshDeclarationSubmissionContext` below: either of the two failure shapes
 * `attemptSubmit` already handled in place (a rejecting/failed read, or the guard's own re-check of
 * `resolveResumeTarget` disagreeing with "declaration"), or the data a real submission write needs. */
type FreshDeclarationSubmissionContext =
  | { readonly outcome: "read-failed" }
  | { readonly outcome: "target-mismatch" }
  | {
      readonly outcome: "ready";
      readonly targetStep: StepId;
      readonly sections: SectionCompletionStatus[];
    };

/**
 * ADR-20261022-cgx7: the fresh-read half of `attemptSubmit` (OBJ-109/FIND-154, extended by
 * OBJ-110/FIND-156 and ADR-20261016-g3kt), pulled out to a standalone function so the branching it
 * resolves -- a rejecting port promise, a returned `Failed` outcome, and the guard's own re-check of
 * `resolveResumeTarget` -- is assessed on its own rather than folded into
 * `DeclarationRouteWrapper`'s cognitive complexity. Pure aside from the port reads it awaits: no
 * state setter, no navigation, no `requestReload` -- the caller still owns every side effect, so
 * behaviour (including the FIND-156 catch-into-"read-failed" mapping) is unchanged.
 */
async function loadFreshDeclarationSubmissionContext(
  renewalSummaryPort: RenewalSummaryPort,
  workingCopyPort: WorkingCopyPort,
  readsAssignments: boolean,
): Promise<FreshDeclarationSubmissionContext> {
  let summaryOutcome: Awaited<ReturnType<RenewalSummaryPort["readSummary"]>>;
  let workingCopyOutcome: Awaited<ReturnType<WorkingCopyPort["readConfirmedSections"]>>;
  let assignmentsOutcome: Awaited<ReturnType<typeof listAssignments>> | undefined;
  try {
    [summaryOutcome, workingCopyOutcome, assignmentsOutcome] = await Promise.all([
      renewalSummaryPort.readSummary(RENEWAL_ID),
      workingCopyPort.readConfirmedSections(RENEWAL_ID),
      readsAssignments ? listAssignments() : Promise.resolve(undefined),
    ]);
  } catch {
    // FIND-156: a rejecting port promise must land in the same branch a returned
    // `{status:"Failed"}` outcome takes below, never escape as an unhandled rejection.
    return { outcome: "read-failed" };
  }
  if (summaryOutcome.status === "Failed" || workingCopyOutcome.status === "Failed") {
    return { outcome: "read-failed" };
  }

  const confirmedAnswers = toConfirmedAnswers(workingCopyOutcome.value.confirmedSections);
  const renewalData = toRenewalData(summaryOutcome.value.policiesInScope);
  const sectionStates = computeSectionStates(SECTION_TABLE, STEP_TABLE, confirmedAnswers, renewalData);
  // AC-21: a fresh read of every currently-active assignment, so a delegated section a colleague
  // has completed reports "completed-by-colleague" here -- never read from the component's own
  // state (it keeps none). DEBT-20261103-snze: assignments are keyed by section instance, so the
  // section list's own rows decide settlement, exactly as the step guard's `loadGuardContext`
  // does: a repeated section is settled once every instance is confirmed or completed by a
  // colleague, whichever did each one.
  const assignments = assignmentsOutcome?.ok === true ? assignmentsOutcome.value : [];
  const instances = expandSectionInstances(summaryOutcome.value, confirmedAnswers);
  const completedByColleagueSectionIds = new Set(
    buildSectionListRows(instances, confirmedAnswers, assignments)
      .filter((row) => row.status === "completed-by-colleague")
      .map((row) => decodeSectionInstanceId(row.instanceId).sectionId),
  );
  const settledSectionStates = applySectionListStatus(sectionStates, instances, confirmedAnswers, assignments);
  const target = resolveResumeTarget(settledSectionStates, STEP_TABLE);
  if (target.targetStep !== "declaration") {
    // The fresh read disagrees with what admitted the customer to this route: something changed
    // underneath them. Do not latch a claim about a step the customer never actually submitted --
    // the caller reloads and lets the guard re-resolve where they actually belong.
    return { outcome: "target-mismatch" };
  }

  const sections = classifySectionCompletionStatuses(settledSectionStates, confirmedAnswers, completedByColleagueSectionIds);
  return { outcome: "ready", targetStep: target.targetStep, sections };
}

/** The caller-owned side effects `handleDeclarationSubmissionOutcome` below applies -- passed in
 * rather than closed over, so the outcome-interpretation branching stays a standalone function
 * (ADR-20261022-cgx7) while every state setter and navigation call remains exactly the one
 * `DeclarationRouteWrapper` already owned before this refactor. */
interface DeclarationSubmissionOutcomeCallbacks {
  readonly markStepSettled: (step: StepId) => void;
  readonly navigate: (path: string) => void;
  readonly requestReload: () => void;
  readonly setServerRefusedPermission: (value: boolean) => void;
  readonly setServerFieldErrors: (value: DeclarationServerFieldErrors | undefined) => void;
  /** m1-portal-ux-fixes Fix C: carries WHICH outcome (never a bare
   * boolean), so `DeclarationRoute` can render the right one of the two
   * distinct Failed/Indeterminate panels. */
  readonly setSubmitRejected: (value: "Failed" | "Indeterminate" | undefined) => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-sev7): records, for the
   * REST OF THIS SESSION, that the renewal has been submitted -- see
   * `markRenewalSubmitted`'s own doc comment on `AppDataContextValue`. */
  readonly markRenewalSubmitted: () => void;
}

/** Shared by both of `handleDeclarationSubmissionOutcome`'s "this caller's write landed" branches
 * (the direct `Succeeded` case and the AC-6 status-read fallback below) -- same
 * markStepSettled-then-advance treatment either way. Pulled out to its own function rather than
 * inlined twice: also lowers the enclosing function's cognitive complexity (sonarjs S3776). */
function advanceToNextStep(
  targetStep: StepId,
  callbacks: Pick<DeclarationSubmissionOutcomeCallbacks, "markStepSettled" | "navigate" | "markRenewalSubmitted">,
): void {
  callbacks.markRenewalSubmitted();
  callbacks.markStepSettled(targetStep);
  const path = nextStepPath(targetStep);
  if (path !== undefined) {
    callbacks.navigate(path);
  }
}

/**
 * ADR-20261022-cgx7: the post-submit interpretation half of `attemptSubmit` -- the 403/422/409
 * taxonomy switch and the AC-6 fallback status read -- pulled out to a standalone function for the
 * same reason as `loadFreshDeclarationSubmissionContext` above. Same branches, same order, same
 * single AC-6 status read; only the side effects are now applied through `callbacks` instead of
 * directly closing over component state.
 */
async function handleDeclarationSubmissionOutcome(
  submissionOutcome: Awaited<ReturnType<DeclarationSubmissionPort["submit"]>>,
  targetStep: StepId,
  callbacks: DeclarationSubmissionOutcomeCallbacks,
): Promise<void> {
  if (submissionOutcome.status === "Succeeded") {
    advanceToNextStep(targetStep, callbacks);
    return;
  }

  const detail = submissionOutcome.detail;
  const failureDetail = detail as DeclarationSubmissionFailureDetail;

  // 403: the write itself refused permission (a defensive, server-confirmed case -- the
  // capability check in the caller already keeps an ordinary contributor off this route).
  if (detail.httpStatus === 403) {
    callbacks.setServerRefusedPermission(true);
    return;
  }

  // 422 invalid_declaration: field errors, shown in screen 6's error summary -- stay on screen 6.
  if (detail.taxonomyCode === DECLARATION_SUBMISSION_FAILURE_CODES.invalidDeclaration) {
    callbacks.setServerFieldErrors(toDeclarationServerFieldErrors(failureDetail.fieldErrors));
    return;
  }

  // 422 incomplete_sections (the server-side completeness backstop, ADR-20261022-yyjf), OR the
  // stub's own untyped 422 (its own test suite's exact shape carries no taxonomyCode at all, and is
  // ALWAYS a section-incompleteness refusal by construction) -- returns the owner to the section
  // list, the same in-place treatment the caller already uses when the fresh read disagrees with
  // the guard's resolved target.
  if (
    detail.taxonomyCode === DECLARATION_SUBMISSION_FAILURE_CODES.incompleteSections ||
    (detail.httpStatus === 422 && detail.taxonomyCode === undefined)
  ) {
    callbacks.requestReload();
    callbacks.navigate("/");
    return;
  }

  // 409 renewal_submitted: the renewal was already submitted (a race, or a second session) --
  // routes straight to the renewal-status view, no further read needed. Also marks the
  // session-level flag (Fix D) so a LATER in-app navigation back to an editable route this
  // session redirects too, not only this one request.
  if (detail.taxonomyCode === DECLARATION_SUBMISSION_FAILURE_CODES.renewalSubmitted) {
    callbacks.markRenewalSubmitted();
    callbacks.navigate("/renewal-status");
    return;
  }

  // AC-6: any other outcome (Indeterminate from a dropped connection, 503 submission_unavailable,
  // or any other unrecognised refusal) triggers exactly one GET /api/portal/submission/status.
  // A "submitted" answer here means THIS caller's own write landed after all (AC-6: "submitted
  // shows screen 7 owner-submitted") -- the same outcome the "Succeeded" branch above reaches, not
  // the 409 branch's "someone/something else already submitted it" case, so it takes the identical
  // markStepSettled-then-advance treatment rather than the 409 branch's `/renewal-status`.
  const statusOutcome = await readSubmissionStatus();
  if (statusOutcome.status === "Succeeded" && statusOutcome.value.submitted) {
    advanceToNextStep(targetStep, callbacks);
    return;
  }
  // Not submitted, or the status read itself failed: stay on screen 6, entries intact
  // (DeclarationRoute never unmounts here), with the stated "not submitted" message -- never
  // markStepSettled, never navigate. `submissionOutcome.status` is "Failed" or "Indeterminate"
  // here (the "Succeeded" case returned at the top of this function).
  callbacks.setSubmitRejected(submissionOutcome.status);
}

/** ADR-20261022-cgx7: `DeclarationRouteWrapper`'s own `onBack` (OBJ-123/FIND-181), pulled out to a
 * standalone function for the same reason as the two above -- the async arrow it replaces was a
 * nested closure whose own branching folded into the component's cognitive complexity. Same fresh
 * read, same `setPendingBackExpand`-before-`navigate` ordering, no behaviour change. */
async function navigateBackFromDeclaration(
  renewalSummaryPort: RenewalSummaryPort,
  workingCopyPort: WorkingCopyPort,
  renewalData: RenewalData,
  confirmedAnswers: ConfirmedAnswers,
  navigate: (path: string) => void,
  setPendingBackExpand: (target: { stepId: StepId; sectionId: SectionId } | undefined) => void,
): Promise<void> {
  const backTarget = await resolveFreshBackTarget("declaration", renewalSummaryPort, workingCopyPort, {
    renewalData,
    confirmedAnswers,
  });
  if (backTarget === undefined) {
    return;
  }
  // OBJ-123/FIND-181: record the resolved "last exposures section" (when answered "yes") BEFORE
  // navigating, so exposures's own route reads it on arrival instead of seeding every section
  // collapsed.
  setPendingBackExpand(
    backTarget.sectionId !== undefined ? { stepId: backTarget.stepId, sectionId: backTarget.sectionId } : undefined,
  );
  navigate(backTarget.path);
}

function DeclarationRouteWrapper() {
  const {
    captureState,
    markStepSettled,
    requestReload,
    renewalSummaryPort,
    workingCopyPort,
    declarationSubmissionPort,
    setPendingBackExpand,
    markRenewalSubmitted,
  } = useAppData();
  const navigate = useNavigate();
  const capability = useCapability();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // AC-21: set only once the submission PORT itself refuses or fails --
  // distinct from `submitFailed` below, which means the fresh READ this
  // wrapper issues before submitting failed outright. Cleared on every
  // new attempt. m1-portal-ux-fixes Fix C: carries WHICH outcome, not a
  // bare boolean -- see `DeclarationRouteProps.notSubmitted`'s own doc
  // comment.
  const [submitRejected, setSubmitRejected] = useState<"Failed" | "Indeterminate" | undefined>(undefined);
  // TG-4 (m1-declaration-next-steps): the real submission write's own 422
  // `invalid_declaration` field errors, mapped onto screen 6's existing error-registry strings
  // (no new copy) and shown in the error summary (WCAG SC 3.3.1/3.3.3).
  const [serverFieldErrors, setServerFieldErrors] = useState<DeclarationServerFieldErrors | undefined>(undefined);
  // TG-4: a defensive 403 from the write itself (a race against the capability check above, which
  // already keeps a contributor off this route in the ordinary case).
  const [serverRefusedPermission, setServerRefusedPermission] = useState(false);
  // TG-4: `attemptSubmit` is also used, with NO argument, as the pre-submit failure shell's own
  // retry (`onRetry` below) -- this remembers the last real submission the customer entered so a
  // retry of the PRE-SUBMIT read still carries it through to the actual submit, rather than
  // silently submitting with an empty name the second time the read succeeds.
  const lastSubmissionRef = useRef<DeclarationSubmission | undefined>(undefined);
  // OBJ-R-3: `isSubmitting` is cleared the moment the write's own promise settles, before the
  // status read `handleDeclarationSubmissionOutcome` issues has resolved and decided where to
  // navigate -- that gap re-enables "Submit renewal" with no reentrancy guard. A second click
  // landing in that gap starts a SECOND `attemptSubmit` whose own, independent status read can
  // resolve and navigate after the first attempt's does, stranding the customer on
  // `/renewal-status` instead of the first attempt's own `/renewal/received`. This ref spans the
  // WHOLE call -- entry to the end of the outcome handling below -- and is deliberately a ref, not
  // state: it must be read and set synchronously inside the same call, with no re-render in
  // between, which a `useState` flag cannot guarantee.
  const submitInFlightRef = useRef(false);
  // OBJ-109/FIND-154: a submit that reads `captureState`'s SNAPSHOT (this
  // component's own render-time data, last refreshed on some earlier
  // `reloadToken` bump) certifies a target resolved from data that can be
  // stale relative to the fresh read the guard just used to admit the
  // customer to this very route — concretely, a committed-but-Indeterminate
  // write (ADR-20260821-kp45) that this file deliberately does not reload
  // for. `submitFailed` surfaces the one new failure mode this fresh read
  // adds (the read itself failing) with the same in-place `InlineReloadFailure`
  // shape every other failed read in this file already uses.
  const [submitFailed, setSubmitFailed] = useState(false);

  // ADR-20261005-avey (realised here, per ADR-20261007-wkxq), product
  // decision 2026-10-07: the truthfulness declaration is a whole-screen
  // question, not a per-control one -- DeclarationRoute renders its five
  // controls as one static unit with no internal gating of its own, so a
  // session that cannot make the declaration gets no declaration route at
  // all, not a disabled, redirected or partially-rendered version of it.
  // Navigation/redirect UX for a contributor who reaches this path
  // directly is out of scope here, left to a forthcoming feature.
  // Code review 2026-10-14: the screen carries the submit control too, so
  // it needs canSubmitRenewal (US-SBC-004) as well as the declaration
  // flag; gating on the declaration flag alone left submission decided by
  // the wrong obligation once the two diverge.
  // ADR-20261015-j9gq (m1-portal-header-footer, AC-10/AC-11, OBJ-1): the gated
  // session still gets the shared header and footer, with an empty main
  // and no declaration controls.
  // ADR-20261015-c9zb (TG-6, AC-19, AC-33) supersedes j9gq's empty main for
  // this refusal: the not-permitted view, with the shared header and
  // footer, and no "Save and finish later" control.
  if (!capability.canMakeTruthfulnessDeclaration || !capability.canSubmitRenewal) {
    return <NotPermittedPage onReturnToRenewal={() => navigate("/")} />;
  }

  if (captureState.status === "ready" && captureState.reloadFailed === true) {
    // OBJ-75/FIND-78: same in-place treatment as makeMultiSectionRoute's
    // identical branch — a reload of an already-good snapshot failing is
    // not the "no read has ever succeeded" case handled below.
    return <InlineReloadFailure onRetry={requestReload} onBackToRenewal={() => navigate("/")} />;
  }
  if (captureState.status === "failed") {
    // OBJ-72/FIND-76, extended by OBJ-77/FIND-80: this route previously
    // had no "failed" branch at all, then redirected to "/" once one was
    // added — relying on the landing route's own, later, independent read
    // failing too. On a deep link / refresh with no prior "ready" snapshot,
    // that read can (and, on any single transient failure, will) succeed
    // on its own, rendering the ordinary in-progress landing and leaving
    // "Submit renewal" nowhere for the customer to retry the read that
    // actually failed. Same in-place surface as the `reloadFailed` branch
    // above, so the initial-read and reload-read failures both show a
    // stated failure with a retry, never a silent redirect.
    return <InlineReloadFailure onRetry={requestReload} onBackToRenewal={() => navigate("/")} />;
  }
  if (captureState.status === "loading") {
    return <PendingShell />;
  }
  // OBJ-109/FIND-154, extended by OBJ-110/FIND-156 and OBJ-110/FIND-157: the
  // submit's own fresh read is pulled out into a named function, rather than
  // inlined only in `onSubmit`, so the SAME read — including its failure
  // handling — is what the failure shell's own retry control re-runs. A
  // `catch` around the `Promise.all` routes a rejecting port promise (the
  // fetch-backed network-drop `TypeError` this feature already established
  // as in scope, FIND-35/FIND-84/FIND-86) into the identical `setSubmitFailed`
  // branch the returned-`Failed` outcome takes, so it can never escape as an
  // unhandled rejection with no stated failure and no retry (FIND-156). And
  // because `onRetry` below is this same function rather than a bare
  // `setSubmitFailed(false)`, clicking "Try again" actually reissues the
  // read: a still-failing port leaves the identical failure shell shown
  // (FIND-157's "the control labelled Try again performed no retry" no
  // longer holds), and a recovered port runs the rest of this routine and
  // navigates away — the customer's declaration answers are never discarded
  // into a remounted blank form, because `DeclarationRoute` only unmounts on
  // a successful, re-verified submission.
  // TG-4: threads the form's entered name and agreement through to the real submission write
  // (the discarded parameter this comment used to document is fixed). `onSubmit` always supplies
  // one; the read-failure shell's own `onRetry` below calls this with none, so `lastSubmissionRef`
  // is what carries it across that retry.
  // ADR-20261022-cgx7: the fresh-read, section-classification and outcome-interpretation branching
  // this function used to carry inline now lives in `loadFreshDeclarationSubmissionContext` and
  // `handleDeclarationSubmissionOutcome` above (both standalone, both unchanged in logic and
  // order) -- this function is now the thin orchestrator that sequences them, same as before.
  const attemptSubmit = async (submission?: DeclarationSubmission): Promise<void> => {
    // OBJ-R-3: refuses a second, overlapping call outright -- including a second click landing
    // after `setIsSubmitting(false)` below but before this same call's own outcome handling has
    // navigated away. Released in `finally`, so a failed or short-circuited attempt (read-failed,
    // target-mismatch) never leaves the guard stuck closed.
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    try {
      if (submission !== undefined) {
        lastSubmissionRef.current = submission;
      }
      if (captureState.status !== "ready") {
        return;
      }
      setSubmitRejected(undefined);
      setServerFieldErrors(undefined);
      setServerRefusedPermission(false);

      // OBJ-109/FIND-154: resolve the step this submission certifies from a FRESH read of both
      // ports, never from `captureState`'s snapshot -- see
      // `loadFreshDeclarationSubmissionContext`'s own doc comment. Capability.canSubmitRenewal is
      // read synchronously, so whether this owner session should even ask for assignments is
      // known up front (ADR-20261016-g3kt).
      const readsAssignments = capability.canSubmitRenewal;
      const context = await loadFreshDeclarationSubmissionContext(renewalSummaryPort, workingCopyPort, readsAssignments);

      if (context.outcome === "read-failed") {
        setSubmitFailed(true);
        return;
      }
      if (context.outcome === "target-mismatch") {
        requestReload();
        navigate("/");
        return;
      }
      setSubmitFailed(false);

      setIsSubmitting(true);
      const effectiveSubmission = submission ?? lastSubmissionRef.current;
      const submissionOutcome = await declarationSubmissionPort.submit({
        renewalId: RENEWAL_ID,
        capability: { canSubmitRenewal: capability.canSubmitRenewal },
        sections: context.sections,
        representativeName: effectiveSubmission?.representativeName,
        agreed: effectiveSubmission?.agreed,
      });
      setIsSubmitting(false);

      await handleDeclarationSubmissionOutcome(submissionOutcome, context.targetStep, {
        markStepSettled,
        navigate,
        requestReload,
        setServerRefusedPermission,
        setServerFieldErrors,
        setSubmitRejected,
        markRenewalSubmitted,
      });
    } finally {
      submitInFlightRef.current = false;
    }
  };

  if (submitFailed) {
    // OBJ-109/FIND-154, FIND-157: `onRetry` is `attemptSubmit` itself — the
    // same fresh read `onSubmit` issues — not a state reset that issues no
    // read at all.
    return <InlineReloadFailure onRetry={() => void attemptSubmit()} onBackToRenewal={() => navigate("/")} />;
  }

  // TG-4: a defensive 403 from the write itself -- the shared not-permitted view, same as the
  // capability-based refusal at the top of this component.
  if (serverRefusedPermission) {
    return <NotPermittedPage onReturnToRenewal={() => navigate("/")} />;
  }

  const stepperItems = atStage(
    buildStepperItems(captureState.confirmedAnswers, captureState.renewalData, navigate, ["exposures"]),
    "declaration",
  );
  const todayDisplay = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <DeclarationRoute
      businessName={captureState.summary.businessName}
      todayDisplay={todayDisplay}
      stepperItems={stepperItems}
      isSubmitting={isSubmitting}
      {...(submitRejected !== undefined ? { notSubmitted: submitRejected } : {})}
      serverFieldErrors={serverFieldErrors}
      onSubmit={(submission) => void attemptSubmit(submission)}
      // AC-33: an owner's "Save and finish later" on screen 6 shows screen
      // 7's saved state, as on screens 3 and 4&5 (ADR-20261016-q2mt).
      onSaveAndFinishLater={() => navigate("/renewal/received?outcome=saved")}
      // ADR-20261001-rz8p (TG-18), narrowed by OBJ-133/FIND-196: resolved from the shared
      // back-navigation function against a FRESH read -- never a hardcoded "exposures" path, and
      // never the already-loaded `captureState` snapshot alone (see `navigateBackFromDeclaration`'s
      // own doc comment, ADR-20261022-cgx7).
      onBack={() =>
        void navigateBackFromDeclaration(
          renewalSummaryPort,
          workingCopyPort,
          captureState.renewalData,
          captureState.confirmedAnswers,
          navigate,
          setPendingBackExpand,
        )
      }
    />
  );
}

/**
 * TG-5/AC-22, resolving DEBT-20261015-k2qh: `businessName`, `ownerName`
 * and `renewalDate` now come from `RenewalSummaryPort` (TG-5's own
 * extension of its contract) instead of the retired "your business"
 * literal.
 *
 * Which of the four H2 states to show: `capability.canSubmitRenewal`
 * (never a role, per the capability boundary) picks owner vs colleague;
 * the `outcome` query parameter -- `"saved"` or the default `"submitted"`
 * -- picks which of that role's two states, set by whichever navigation
 * reached this route (confirm-details' "Save and finish later" sets
 * `outcome=saved` below; the declaration screen's real submit navigates
 * with no parameter, the default). Carries no PII (DEBT-20261012-zytp,
 * the declaration screen's own submit-outcome wiring, is a separate,
 * already-tracked gap this wrapper does not attempt to close).
 */
function ReceivedRouteWrapper() {
  const { captureState, requestReload } = useAppData();
  const navigate = useNavigate();
  const capability = useCapability();
  const [searchParams] = useSearchParams();
  if (captureState.status === "ready" && captureState.reloadFailed === true) {
    // OBJ-75/FIND-78: same in-place treatment as the other step routes.
    return <InlineReloadFailure onRetry={requestReload} onBackToRenewal={() => navigate("/")} />;
  }
  if (captureState.status === "failed") {
    // OBJ-72/FIND-76, extended by OBJ-77/FIND-80: previously grouped with
    // "loading" and shown `PendingShell` (a failed read rendered as a
    // perpetual "Loading…"), then redirected to "/" — which relied on the
    // landing route's own, later, independent read failing too, and does
    // not hold on a deep link / refresh with no prior "ready" snapshot.
    // Same in-place surface as every other failed-read branch in this
    // file now.
    return <InlineReloadFailure onRetry={requestReload} onBackToRenewal={() => navigate("/")} />;
  }
  if (captureState.status !== "ready") {
    return <PendingShell />;
  }
  const isOwner = capability.canSubmitRenewal;
  const isSaved = searchParams.get("outcome") === "saved";
  let state: "owner-saved" | "owner-submitted" | "colleague-saved" | "colleague-submitted";
  if (isOwner) {
    state = isSaved ? "owner-saved" : "owner-submitted";
  } else {
    state = isSaved ? "colleague-saved" : "colleague-submitted";
  }
  return (
    <ReceivedRoute
      businessName={captureState.summary.businessName}
      renewalDate={captureState.summary.renewalDate}
      policiesInScope={captureState.summary.policiesInScope}
      ownerName={captureState.summary.ownerName}
      state={state}
    />
  );
}

function SignInRoute() {
  const navigate = useNavigate();
  // ADR-20261102-k4rf: carry the renewal reference on, so "Enter your email address again" keeps it.
  return <SignInPage onRequested={() => navigate(withSignInRef("/sign-in/check-email", window.location.search))} />;
}

function RedeemRoute() {
  const navigate = useNavigate();
  return (
    <RedeemPage
      onRedeemed={() => navigate("/")}
      onReadOnly={() => navigate("/renewal-status")}
      // ADR-20261015-6w29 (AC-9): a spent link opened by a customer who
      // already holds a live session goes to the same screen a freshly
      // redeemed token does -- no new session is created (the server
      // already refused to create one), so this is a navigation to the
      // existing session's screen 2, not a second "redeemed" outcome.
      onActiveSession={() => navigate("/")}
      onNotFound={() => navigate("/sign-in/link-unavailable")}
      // ADR-20261016-f8ws (design state 1.5): the same destination
      // LinkUnavailableRoute's own action navigates to -- "Get a new
      // link" always opens the sign-in view, regardless of which of the
      // two views showed it.
      onGetNewLink={() => navigate("/sign-in")}
    />
  );
}

function LinkUnavailableRoute() {
  const navigate = useNavigate();
  return <LinkUnavailablePage onGetNewLink={() => navigate(`/sign-in?${SIGN_IN_ORIGIN_PARAM}=${LINK_UNAVAILABLE_ORIGIN}`)} />;
}

function NotPermittedRoute() {
  const navigate = useNavigate();
  return <NotPermittedPage onReturnToRenewal={() => navigate("/")} />;
}

function NotFoundRedirect() {
  // An unregistered client-side path is never a 404 in an SPA (the
  // server already answered it with the shell via the static-web-app's
  // navigation fallback) — sending it to the landing route keeps that
  // guarantee without rendering React Router's own default error view.
  // This is not "an automatic redirect past the landing route" (the
  // human direction above): the landing route IS the destination here,
  // never bypassed.
  return <Navigate to="/" replace />;
}

// TG-18 (ADR-20261001-rjj8): confirm-details and change-check are each
// served by their own dedicated, single-section route component
// (TG-15/TG-16) rather than the generic makeMultiSectionRoute renderer
// TG-13 left them on transitionally — the compiler's own
// `Record<StepId, ComponentType>` enforces every StepId stays covered as
// this mapping changes.
// AC-24: every step route is claim-bearing (the declaration and read-only/
// assigned-section rendering all read from the resolved capability), so
// each entry is wrapped with `requireSession` here, at the one place this
// mapping is assembled, rather than inside each wrapper function.
// AC-23: every step route except `received` also carries
// `requireSessionAndWindowOpen` -- once the renewal has been submitted or
// saved, the window's own open/closed state no longer gates anything for
// that customer, so `received` keeps plain `requireSession`.
const STEP_ROUTE_COMPONENTS: StepRouteComponents = {
  // Fix D: these three are the editable routes `requireNotSubmitted` names
  // -- "change-check" is a redirect-only shell (never itself editable) and
  // "received" is the terminal, already-submitted/-saved view, so neither
  // needs the extra guard.
  "confirm-details": requireSessionAndWindowOpen(requireNotSubmitted(ConfirmDetailsRouteWrapper)),
  "change-check": requireSessionAndWindowOpen(ChangeCheckRedirectRoute),
  exposures: requireSessionAndWindowOpen(requireNotSubmitted(makeMultiSectionRoute("exposures"))),
  declaration: requireSessionAndWindowOpen(requireNotSubmitted(DeclarationRouteWrapper)),
  received: requireSession(ReceivedRouteWrapper),
};

// Hoisted to named, module-level constants (rather than calling
// `requireSession(...)` inline in the router array below) so each route
// entry stays a single-line `{ path: "...", Component: <identifier>,
// errorElement: <PageErrorShell /> }` shape -- router.test.tsx's own
// source-text check for AC-9/AC-10/AC-11 (m1-portal-header-footer,
// DEBT-20261015-qqwe) matches exactly that shape with a bare `\w+`
// identifier for `Component`, and must keep working without being
// rewritten to understand a wrapped expression.
const GatedLandingRoute = requireSessionAndWindowOpen(LandingRoute);
const GatedRenewalStatusPage = requireSession(RenewalStatusPage);
const GatedNotPermittedRoute = requireSession(NotPermittedRoute);

function App() {
  const [reloadToken, setReloadToken] = useState(0);
  const settledBeyondTargetRef = useRef<StepId | undefined>(undefined);
  // m1-portal-ux-fixes Fix D: see `AppDataContextValue.markRenewalSubmitted`'s own doc comment.
  const renewalSubmittedRef = useRef(false);
  // OBJ-123/FIND-181: see `AppDataContextValue.setPendingBackExpand`'s doc
  // comment. Plain in-memory ref, scoped to this one `App` instance — never
  // written to the URL, to `history.state`, or to a `navigate()`/`<Link>`
  // `state` option.
  const pendingBackExpandRef = useRef<{ stepId: StepId; sectionId: SectionId } | undefined>(undefined);

  // ADR-20261015-bzyr: this composition root deliberately constructs ONLY
  // the ports a screen actually consumes. DeclarationSubmissionPort is
  // wired in TG-6 of m1-magic-link-portal-refresh: the declaration screen
  // is the first screen that needs it (AC-21). ExposurePrefillPort, which
  // this same rule used to keep unwired, is retired outright
  // (m1-exposure-detail-sourcing TG-4, ADR-20261019-q764) -- its stub,
  // contract and fixture are deleted, not merely left unwired.
  //
  // TG-4 (ADR-20261016-xfwy, m1-epic-landing-summary): `renewalSummaryPort`
  // was the first port this composition root bound to a real upstream
  // rather than an in-memory stub/fixture pair.
  //
  // m1-exposure-detail-sourcing TG-4 (ADR-20261019-q764): `workingCopyPort`
  // now does the same, over the published contract 2.0.0 routes TG-3 of
  // that feature built -- `onFilePort`/`renewalDataPort` are constructed
  // here too, ready for the screens that consume them (a later task group
  // in that same feature), even though no route reads from either yet;
  // unlike ExposurePrefillPort, both have a named, in-progress consumer
  // within the same feature, so this is scaffolding-ahead-of-use, not the
  // "deliberately unwired" exception above.
  //
  // TG-4 (m1-declaration-next-steps, ADR-20261015-bzyr/-6xax/-ew91): `declarationSubmissionPort`
  // is now real too, over the real upstream TG-2/TG-3 of this feature built (`POST
  // /api/portal/submission`). ADR-20261022-ew91 dropped the STUB's `isSampleData: true` from
  // the OR below while the port stayed a stub (BLOCK-002, AC-8); its own closing note said this
  // could be added back "when the declaration port is implemented in a future feature" -- this is
  // that feature, so it is added back immediately below, now correctly reporting `false`.
  const [ports] = useState(() => ({
    renewalSummaryPort: createHttpRenewalSummaryPort(),
    workingCopyPort: createHttpWorkingCopyPort(),
    onFilePort: createHttpOnFilePort(),
    renewalDataPort: createHttpRenewalDataPort(),
    declarationSubmissionPort: createHttpDeclarationSubmissionPort() as DeclarationSubmissionPort & {
      isSampleData: boolean;
    },
  }));

  // ADR-20261016-6xax (AC-30 refresh): the OR of every bound port's own
  // `isSampleData` flag -- the single value `SampleDataContext` carries
  // down to `PortalHeader`. Computed here, once, from the real ports this
  // composition root just constructed; never re-derived or guessed
  // downstream.
  const isSampleData =
    ports.renewalSummaryPort.isSampleData ||
    ports.workingCopyPort.isSampleData ||
    ports.onFilePort.isSampleData ||
    ports.declarationSubmissionPort.isSampleData ||
    ports.renewalDataPort.isSampleData;

  const hasSession = useHasSession();
  const [captureState, applyLandingReload] = useCaptureState(
    ports.renewalSummaryPort,
    ports.workingCopyPort,
    reloadToken,
    hasSession,
  );

  // Constructed exactly once (lazy useState initializer, never useRef —
  // see the test harness's own comment on this same choice: a ref must
  // never be read during render). The guard loader inside does its OWN
  // fresh read on every navigation (router.tsx's own documented "no
  // caching here" contract) rather than reading this component's
  // `captureState`, for the identical race-avoidance reason the harness
  // documents.
  const [dependencies] = useState<RouterDependencies>(() => ({
    loadGuardContext: async (): Promise<StepGuardContext> => {
      // ADR-20261016-q2mt: `resolveCapability` is the sanctioned,
      // capability/-internal boundary for this check (ADR-20260826-79rv's
      // containment rule), read fresh for this same guard evaluation, not
      // cached across navigations. Read up front, synchronously, so the
      // ADR-20261016-g3kt assignments read below (gated on it) starts in
      // the SAME `Promise.all` as the summary/working-copy reads rather
      // than after them -- a real served target's own round-trip latency
      // made a third, sequential network call here measurably widen a
      // pre-existing navigation race around this guard's own redirect
      // (TG-8, diagnosed against a real browser, not reproducible against
      // any mocked-port unit test), even though this call changes no
      // existing read's outcome.
      const isContributorSession = !resolveCapability(getCurrentRawClaim()).canSubmitRenewal;
      const readsAssignments = !isContributorSession;
      const [summaryOutcome, workingCopyOutcome, assignmentsOutcome] = await Promise.all([
        ports.renewalSummaryPort.readSummary(RENEWAL_ID),
        ports.workingCopyPort.readConfirmedSections(RENEWAL_ID),
        readsAssignments ? listAssignments() : Promise.resolve(undefined),
      ]);
      if (summaryOutcome.status === "Failed" || workingCopyOutcome.status === "Failed") {
        // OBJ-73/FIND-73: coercing a `Failed` guard read into "nothing
        // confirmed" resolved the redirect target as confirm-details and
        // rendered it as an ORDINARY, completed step — the AC-9 negative
        // scenario ("a failed read must never be rendered as an ordinary
        // state") on the one read this guard loader owns. router.tsx's own
        // `errorElement` (`RouteErrorShell`) exists precisely for a guard
        // loader that throws anything other than its own `redirect()` —
        // throwing here, rather than substituting empty section states,
        // routes a failed read to that stated-failure shell instead of to
        // an ordinary-looking screen.
        throw new Error("App.tsx: loadGuardContext's read failed");
      }
      const confirmedAnswers: ConfirmedAnswers = toConfirmedAnswers(
        workingCopyOutcome.value.confirmedSections,
      );
      const renewalData: RenewalData = toRenewalData(summaryOutcome.value.policiesInScope);
      const sectionStates = computeSectionStates(SECTION_TABLE, STEP_TABLE, confirmedAnswers, renewalData);
      // ADR-20261016-g3kt: a section a colleague has completed counts as
      // settled for the OWNER's own step order, so "Continue and submit"
      // is never blocked behind a delegated section the owner never
      // personally confirmed. This overrides `state` only on the guard's
      // OWN copy of `sectionStates` -- `computeSectionStates`'s single
      // shared computation is untouched, and nothing here writes to
      // `confirmedAnswers`, so every OTHER reader of section state (the
      // section list, the stepper, ExposuresJourneyRoute's own "Completed
      // by colleague" panel) keeps reporting exactly what it already
      // reports; only the guard's own notion of "settled enough to
      // proceed" changes. The owner may still reopen the section (AC-18).
      //
      // REQ-001 (AC-3): the override is the section list's OWN status
      // (`applySectionListStatus` -> `buildSectionListRows`), not a
      // promotion-only patch. It also DEMOTES: a section assigned to a
      // colleague who has not completed and submitted it shows "Assigned"
      // on the list, so it is unsettled here too, even when the
      // colleague's confirmed page is already in the working copy. Same
      // precedence as `submission_completeness.py`'s server backstop.
      let guardSectionStates: readonly SectionStateEntry[] = sectionStates;
      if (assignmentsOutcome?.ok === true) {
        guardSectionStates = applySectionListStatus(
          sectionStates,
          expandSectionInstances(summaryOutcome.value, confirmedAnswers),
          confirmedAnswers,
          assignmentsOutcome.value,
        );
      }
      return {
        sectionStates: guardSectionStates,
        ...(settledBeyondTargetRef.current !== undefined
          ? { settledBeyondTarget: settledBeyondTargetRef.current }
          : {}),
        isContributorSession,
      };
    },
    // OBJ-74/FIND-77: `setReloadToken`'s identity is stable across
    // renders (React's own `useState` setter contract), so capturing it
    // once here in this lazy initializer is safe — this is the SAME
    // reload every step route already requests via `onSectionConfirmed`,
    // never a second mechanism invented for the error shell.
    onRetry: () => setReloadToken((count) => count + 1),
  }));

  const [router] = useState(() =>
    createBrowserRouter([
      // DEBT-20261015-qqwe: a render error on a non-step route keeps the
      // shared header and footer (step routes carry RouteErrorShell).
      //
      // AC-24: "/", "/renewal-status" and "/renewal/not-permitted" are
      // claim-bearing routes, wrapped with `requireSession` (as the
      // module-level Gated* constants below) -- a request without a live
      // session shows state 1.1 (the sign-in route) and renders no
      // claim-derived content. The five routes below this comment
      // (/sign-in and its three siblings, /redeem) are reached BEFORE a
      // session exists by construction and are deliberately never
      // wrapped.
      { path: "/", Component: GatedLandingRoute, errorElement: <PageErrorShell /> },
      { path: "/sign-in", Component: SignInRoute, errorElement: <PageErrorShell /> },
      { path: "/sign-in/check-email", Component: CheckEmailPage, errorElement: <PageErrorShell /> },
      { path: "/sign-in/link-unavailable", Component: LinkUnavailableRoute, errorElement: <PageErrorShell /> },
      { path: "/redeem", Component: RedeemRoute, errorElement: <PageErrorShell /> },
      { path: "/renewal-status", Component: GatedRenewalStatusPage, errorElement: <PageErrorShell /> },
      { path: "/renewal/not-permitted", Component: GatedNotPermittedRoute, errorElement: <PageErrorShell /> },
      ...buildStepRoutes(STEP_ROUTE_COMPONENTS, dependencies),
      { path: "*", Component: NotFoundRedirect, errorElement: <PageErrorShell /> },
    ]),
  );

  // ADR-20261008-jg8z (AC-9): registered once the router exists, so the
  // handler can navigate without being a component of its own.
  // `router.navigate(to)` is called with no second argument here -- never
  // a `state` option (ADR-20261006-cp4y / OBJ-92).
  useEffect(() => {
    registerSessionEndedHandler(() => {
      void router.navigate("/sign-in/link-unavailable");
    });
  }, [router]);

  const contextValue: AppDataContextValue = useMemo(
    () => ({
      renewalSummaryPort: ports.renewalSummaryPort,
      workingCopyPort: ports.workingCopyPort,
      renewalDataPort: ports.renewalDataPort,
      onFilePort: ports.onFilePort,
      declarationSubmissionPort: ports.declarationSubmissionPort,
      captureState,
      requestReload: () => setReloadToken((count) => count + 1),
      applyLandingReload,
      markStepSettled: (stepId) => {
        settledBeyondTargetRef.current = stepId;
      },
      isStepSettled: (stepId) => settledBeyondTargetRef.current === stepId,
      onWorkingCopyWriteSucceeded: () => {
        settledBeyondTargetRef.current = undefined;
      },
      setPendingBackExpand: (target) => {
        pendingBackExpandRef.current = target;
      },
      consumePendingBackExpand: (stepId) => {
        const pending = pendingBackExpandRef.current;
        pendingBackExpandRef.current = undefined;
        return pending?.stepId === stepId ? pending.sectionId : undefined;
      },
      markRenewalSubmitted: () => {
        renewalSubmittedRef.current = true;
      },
      isRenewalSubmitted: () => renewalSubmittedRef.current,
    }),
    [
      ports.renewalSummaryPort,
      ports.workingCopyPort,
      ports.renewalDataPort,
      ports.onFilePort,
      ports.declarationSubmissionPort,
      captureState,
      applyLandingReload,
    ],
  );

  // TG-3: CapabilityProvider no longer wraps the whole router -- it is
  // mounted per claim-bearing route instead (`requireSession`, above),
  // because the anonymous pre-session routes (/sign-in and its siblings,
  // /redeem) are reached before any session exists and must render
  // regardless of whether one does (AC-24).
  return (
    <SampleDataContext.Provider value={isSampleData}>
      <AppDataContext.Provider value={contextValue}>
        <RouterProvider router={router} />
      </AppDataContext.Provider>
    </SampleDataContext.Provider>
  );
}

export default App;
